import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import Modal from "react-modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Loader from "../../components/Loader";
import DropDown from "../../assets/svgIcons/PrimaryDropDown.svg";
import { Pagination } from "../../components/Pagination";
import AddNewButton from "../../components/AddNewButton";
import DownloadButton from "../../components/DownloadButton";
import WorkOrderSalesOrderDownload from "../../components/WorkOrderSalesOrderDownload";
import ProductMaster from "../../components/master/ProductMaster";
import VendorMasterDataModal from "../../components/master/VendorMasterDataModal";
import CustomerMasterDataModal from "../../components/master/CustomerMasterDataModal";
import AddNewEntryModal from "../../components/AddNewEntryModal";
import Search from "../../assets/svgIcons/Search.svg";
import { displayToast } from "../../utils/Toast";
import { useOutsideClick } from "../../utils/useOutsideClick";
import {
  capitalizeFirstLetterOfEachWord,
  formatDate,
  formatFileUrlValue,
  cleanReportName,
} from "../../utils/Formatter";
import { Calendar, RotateCcw, ClipboardList, ArrowDown } from "lucide-react";
import { fetchReportsData } from "../../apis/reports";
import { populateDrawingFileNames } from "../../apis/attachments";
import { useErrorHandler } from "../../services/ErrorHandler";
import {
  fetchCustomerOrVendor,
  fetchCustomerOrVendorwithProduct,
  fetchSubcategoriesByCategory,
  fetchVendorsBySubcategory,
} from "../../apis/transaction";
import {
  fetchVendorCategories,
  fetchAnalysisReportPage,
  fetchFilteredTransactionByCode,
  fetchApprovalTransactionDetails,
  fetchTransactionSettingsByCode,
  updateTransactionSettings,
  insertTransactionByCode,
  fetchTransactionNo,
  fetchBomByProduct,
} from "../../apis/transaction";
import { fetchTransactionByCodeAndId } from "../../apis/transaction";
import { getSettingsData } from "../../apis/tabContentDetailApis";
import { fetchMasterTableData } from "../../apis/transaction";
import { usePermissions } from "../../hooks/usePermissions";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import {
  formatMasterType,
  MASTER_MENU_TYPES,
} from "../../constants/masterMenus";
import {
  useListPageCache,
  generateCacheKey,
  getCachedDataDirect,
} from "../../context/ListPageCacheContext";
import "../../styles/pages/tabsDynamic/TabContentDetail.css";
import "../../styles/components/MasterDataModal.css";

const toLocalYMD = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const AnalysisDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      className="analysis-date-input-box"
      onClick={onClick}
      ref={ref}
      tabIndex={0}
    >
      <span
        className={value ? "analysis-date-value" : "analysis-date-placeholder"}
      >
        {value || placeholder}
      </span>
      <Calendar size={16} className="analysis-date-icon" />
    </div>
  ),
);

const ColumnDateFilterInput = React.forwardRef(
  ({ value, onClick, placeholder, title }, ref) => (
    <div
      className={`column-filter-date-input${value ? " has-value" : ""}`}
      onClick={onClick}
      ref={ref}
      tabIndex={0}
      role="button"
      title={title}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(e);
        }
      }}
    >
      <span className="column-filter-date-text">{value || placeholder}</span>
      <Calendar size={13} className="column-filter-date-icon" />
    </div>
  ),
);

const isDateLikeHeading = (heading) => {
  const fieldName = String(heading?.field_name || "").toLowerCase();
  const displayName = String(heading?.display_name || "").toLowerCase();
  if (/date_range|date_type|update/.test(fieldName)) return false;
  return (
    fieldName.includes("date") ||
    fieldName.endsWith("_dt") ||
    fieldName.endsWith("_at") ||
    /\bdate\b|\bdt\b/.test(displayName)
  );
};

/* Keeps the calendar fully within the viewport when it flips upward */
const CALENDAR_MODIFIERS = [
  {
    name: "shiftY",
    fn({ y, placement }) {
      if (String(placement).startsWith("top")) {
        return { y: Math.max(10, y) };
      }
      return {};
    },
  },
];

const QUOTATION_STATUS_LEGEND_ITEMS = [
  {
    key: "quotation-po",
    swatch: "quotation-po",
    label: "PO Recieved",
  },
  {
    key: "quotation-rejected",
    swatch: "quotation-rejected",
    label: "Rejected",
  },
  {
    key: "quotation-submitted",
    swatch: "quotation-submitted",
    label: "Quote submitted",
  },
  {
    key: "quotation-clarification",
    swatch: "quotation-clarification",
    label: "Clarification sent",
  },
];

const getQuotationStatusTone = (status) => {
  const normalizedStatus = String(status ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (
    normalizedStatus.startsWith("poreceiv") ||
    normalizedStatus.startsWith("poreciev")
  ) {
    return "po";
  }
  if (normalizedStatus.includes("rejected")) return "rejected";
  if (normalizedStatus.includes("quotesubmitted")) return "submitted";
  if (normalizedStatus.includes("clarification")) return "clarification";
  return "";
};

const formatNavigationPath = (path) => {
  return path.replace(/\//g, "-");
};

const buildDocumentRefNo = (settings, fallbackTranCode) => {
  const refCode = settings?.ref_code || settings?.tran_code || fallbackTranCode;
  const tranCode = settings?.tran_code || fallbackTranCode;
  if (!refCode || !tranCode) {
    return settings?.ref_no || "";
  }
  try {
    const items = JSON.parse(localStorage.getItem("sidebarItems") || "[]");
    const parent = items.find((p) =>
      (p.child_menu || []).some((c) => c.menu_code === tranCode),
    );
    const children = parent?.child_menu || [];
    const index = children.findIndex((c) => c.menu_code === tranCode);
    const order = index >= 0 ? index + 1 : 1;
    return `FP/F/${refCode}/${String(order).padStart(2, "0")}`;
  } catch {
    return settings?.ref_no || `FP/F/${refCode}/01`;
  }
};

const getInitialCachedState = (location, urlTab, categoryParam, pathParts) => {
  const tabValue =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);
  const categoryValue =
    pathParts[0] === "master"
      ? pathParts.length >= 3
        ? categoryParam
        : undefined
      : categoryParam;

  if (location.state?.forceRefresh === true) {
    return null;
  }

  const sessionStorageKey = `clearCache_${tabValue}_${categoryValue}`;
  if (sessionStorage.getItem(sessionStorageKey) === "true") {
    return null;
  }

  let cacheKey = null;

  if (tabValue && categoryValue) {
    const storedCacheKey = sessionStorage.getItem(
      `cacheKey_${tabValue}_${categoryValue}`,
    );
    if (storedCacheKey) {
      cacheKey = storedCacheKey;
    }
  }

  if (!cacheKey) {
    if (tabValue === "master") {
      const masterType = location.state?.transactionCode;
      const subType = location.state?.masterSubType;
      if (masterType && subType) {
        cacheKey = generateCacheKey(
          "master",
          categoryValue,
          null,
          masterType,
          subType,
        );
      }
    } else if (tabValue !== "reports" && categoryValue) {
      const transactionCodeValue =
        location.state?.transactionCode ||
        sessionStorage.getItem("transactionCode");
      if (transactionCodeValue) {
        cacheKey = generateCacheKey(
          tabValue,
          categoryValue,
          transactionCodeValue,
        );
      }
    }
  }

  if (cacheKey) {
    const cachedData = getCachedDataDirect(cacheKey);
    return cachedData;
  }
  return null;
};

const TabContentDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: urlTab, category: categoryParam } = useParams();
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const [companies, setCompanies] = useState([]);
  const { handleErrorResponse } = useErrorHandler();
  const { reportsDataQuery } = location.state || {};
  const tab =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);

  const category =
    pathParts[0] === "master"
      ? pathParts.length >= 3
        ? categoryParam
        : undefined
      : categoryParam;

  const isAnalysisReportPage = Boolean(tab && !category && tab !== "reports");
  const isProductionAnalysisPage =
    isAnalysisReportPage && String(tab).toLowerCase() === "production";
  const isMasterSubListPage =
    String(tab).toLowerCase() === "master" && pathParts.length === 2;
  const isDrawingStatusPage = ["drawing-status", "dr"].includes(
    String(category || "").toLowerCase(),
  );
  const isChildTransactionListPage =
    Boolean(category) &&
    tab !== "master" &&
    tab !== "approval" &&
    tab !== "reports";
  const isVoucherPaymentList =
    String(category || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "") === "voucherpayment";
  const isEnquiryListPage =
    Boolean(category) &&
    (String(category).toLowerCase() === "enquiry" ||
      String(location.state?.transactionCode || "").toUpperCase() === "SE");

  const transactionCode = isAnalysisReportPage
    ? location.state?.transactionCode
    : location.state?.transactionCode ||
      sessionStorage.getItem("transactionCode");
  const isSalesQuotationList =
    isChildTransactionListPage &&
    String(transactionCode || "").toUpperCase() === "SQ";

  const masterSubTypeValue =
    location.state?.masterSubType ||
    (pathParts[0] === "master" && pathParts.length >= 3
      ? pathParts[2]
      : undefined);
  const masterSubTypes =
    MASTER_MENU_TYPES[String(transactionCode || "").toUpperCase()]?.subTypes ||
    [];
  const isMasterProductsList =
    tab === "master" &&
    String(transactionCode || "").toUpperCase() === "PM" &&
    String(masterSubTypeValue || "").toLowerCase() === "sales";
  const isMasterSubtypeList =
    tab === "master" &&
    masterSubTypes.length > 0 &&
    Boolean(masterSubTypeValue);

  const initialCachedData = getInitialCachedState(
    location,
    urlTab,
    categoryParam,
    pathParts,
  );

  useLayoutEffect(() => {
    if (transactionCode && location.state?.transactionCode) {
      sessionStorage.setItem("transactionCode", transactionCode);
    }
  }, [transactionCode, location.state?.transactionCode]);

  const [loading, setLoading] = useState(false);
  const [columnOrderByPage, setColumnOrderByPage] = useState({});
  const [draggingColumnField, setDraggingColumnField] = useState(null);

  const [tableData, setTableData] = useState(() => {
    if (initialCachedData?.tableData) {
      return initialCachedData.tableData;
    }
    return {
      headings: [],
      rows: [],
      filters: [],
      transactionSettings: [],
      documentFileName: "",
      colorCode: "",
      customerType: "",
    };
  });

  useLayoutEffect(() => {
    if (initialCachedData?.tableData) {
      if (
        !tableData.rows?.length ||
        tableData.rows?.length !== initialCachedData.tableData.rows?.length
      ) {
        setTableData(initialCachedData.tableData);
        setTotalPages(initialCachedData.totalPages || 1);
        setFilteredData(initialCachedData.tableData.rows || []);
      }
    } else {
      if (transactionCode && tab && category) {
        const cacheKey = getCacheKey(
          tab,
          category,
          transactionCode,
          null,
          null,
          currentPage,
        );
        const cachedData = getCachedDataSync(cacheKey);
        if (cachedData?.tableData) {
          setTableData(cachedData.tableData);
          setTotalPages(cachedData.totalPages || 1);
          setFilteredData(cachedData.tableData.rows || []);
          if (cachedData.tableData?.headings?.length > 0) {
            setTableHeadings(cachedData.tableData.headings);
          }
        }
      }
    }
  }, []);

  const [pageSize] = useState(20);
  const tabContentCategory = "sales";
  const [products, setProducts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] = useState("");
  const rights = usePermissions(transactionCode);
  const [totalPages, setTotalPages] = useState(() => {
    return initialCachedData?.totalPages || 1;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState({});
  const [columnFilterDrafts, setColumnFilterDrafts] = useState({});
  const [columnOptions, setColumnOptions] = useState({});
  const [listQueryVersion, setListQueryVersion] = useState(0);
  const [isListRefreshing, setIsListRefreshing] = useState(false);
  const [sortState, setSortState] = useState(null);
  const [selectedRowColors, setSelectedRowColors] = useState([]);
  const columnFiltersRef = useRef({});
  const sortStateRef = useRef(null);
  const selectedRowColorsRef = useRef([]);
  const currentPageRef = useRef(1);
  const columnOptionsLoadedRef = useRef(false);
  const skipNextListQueryRef = useRef(false);
  const listDateFieldsRef = useRef({});
  const [listDateFields, setListDateFields] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const widthsLoadedRef = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableHeadings, setTableHeadings] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const statusOptions = ["approved", "pending", "rejected"];
  const [resizingColumn, setResizingColumn] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isMasterDataModalOpen, setIsMasterDataModalOpen] = useState(false);
  const {
    getCacheKey,
    getCachedData,
    getCachedDataSync,
    setCachedData,
    clearCache,
  } = useListPageCache();

  const getDefaultDateRange = () => {
    const today = new Date();
    const financialYear =
      today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const financialYearStart = new Date(financialYear, 3, 1);

    return {
      fromDate: toLocalYMD(financialYearStart),
      toDate: toLocalYMD(today),
    };
  };

  const appendDummyTime = (date) => {
    return `${date}T00:00:00Z`;
  };

  const normalizeDateOnly = (dateValue) => {
    if (!dateValue) return "";
    return String(dateValue).split("T")[0];
  };

  const getPreferredDateFields = () => {
    if (isDrawingStatusPage) {
      return [
        "wo_date",
        "wo_dt",
        "drawing_release_date",
        "drawing_release_dt",
        "release_date",
        "release_dt",
        "drawing_date",
        "drawing_dt",
        "status_date",
        "date",
        "created_date",
        "updated_date",
        "created_at",
        "updated_at",
        "mail_date",
      ];
    }

    return [
      "date",
      "enquiry_date",
      "quote_date",
      "quotation_date",
      "order_date",
      "invoice_date",
      "po_date",
      "wo_date",
      "wo_dt",
      "drawing_release_date",
      "drawing_release_dt",
      "release_date",
      "release_dt",
      "drawing_date",
      "drawing_dt",
      "status_date",
      "created_date",
      "updated_date",
      "created_at",
      "updated_at",
      "mail_date",
    ];
  };

  const getDrawingStatusDateFields = () => [
    "wo_date",
    "workorder_date",
    "work_order_date",
    "wo_dt",
  ];

  const getListDateFields = async () => {
    if (!listDateFieldsRef.current[transactionCode]) {
      listDateFieldsRef.current[transactionCode] =
        fetchTransactionSettingsByCode(transactionCode)
          .then((settings) => {
            const fields = (settings?.head_view_fields || [])
              .filter(
                (heading) => !heading.is_internal && isDateLikeHeading(heading),
              )
              .map((heading) => heading.field_name);
            setListDateFields(fields);
            return fields;
          })
          .catch(() => {
            delete listDateFieldsRef.current[transactionCode];
            setListDateFields([]);
            return [];
          });
    }
    return listDateFieldsRef.current[transactionCode];
  };

  const getSelectedQuotationStatuses = () =>
    QUOTATION_STATUS_LEGEND_ITEMS.filter((item) =>
      selectedRowColorsRef.current.includes(item.key),
    );

  const buildColumnFilterPayload = (filters = columnFiltersRef.current) =>
    Object.entries(filters || {})
      .filter(
        ([, filter]) => filter && String(filter.value ?? "").trim() !== "",
      )
      .map(([fieldName, filter]) => ({
        field_name: fieldName,
        operation: filter.operation || "%",
        value: String(filter.value).trim(),
      }));

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(1, Number(page) || 1), totalPages || 1);
    if (nextPage === currentPageRef.current) return;
    currentPageRef.current = nextPage;
    setCurrentPage(nextPage);
    document
      .querySelector(".table-heading-wrapper")
      ?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyColumnFilter = (fieldName, value, operation = "%") => {
    const trimmed = String(value ?? "").trim();
    const next = { ...columnFiltersRef.current };
    if (trimmed) {
      next[fieldName] = { value: trimmed, operation };
    } else {
      delete next[fieldName];
    }
    setColumnFilterDrafts((prev) => ({ ...prev, [fieldName]: trimmed }));
    if (JSON.stringify(next) === JSON.stringify(columnFiltersRef.current)) {
      return;
    }
    columnFiltersRef.current = next;
    setColumnFilters(next);
    currentPageRef.current = 1;
    setCurrentPage(1);
    setListQueryVersion((version) => version + 1);
  };

  const clearColumnFilter = (fieldName) => applyColumnFilter(fieldName, "");

  const requestListRefetch = () => {
    currentPageRef.current = 1;
    setCurrentPage(1);
    setListQueryVersion((version) => version + 1);
  };

  const toggleColumnSort = (fieldName) => {
    const current = sortStateRef.current;
    let next = null;
    if (!current || current.field !== fieldName) {
      next = { field: fieldName, direction: "asc" };
    } else if (current.direction === "asc") {
      next = { field: fieldName, direction: "desc" };
    }
    sortStateRef.current = next;
    setSortState(next);
    requestListRefetch();
  };

  const toggleRowColorFilter = (colorKey) => {
    const current = selectedRowColorsRef.current;
    const next = current.includes(colorKey)
      ? current.filter((key) => key !== colorKey)
      : [...current, colorKey];
    selectedRowColorsRef.current = next;
    setSelectedRowColors(next);
    requestListRefetch();
  };

  const handleResetAllFilters = () => {
    const defaults = getDefaultDateRange();
    const datesChanged =
      defaults.fromDate !== filterValues.fromDate ||
      defaults.toDate !== filterValues.toDate;
    columnFiltersRef.current = {};
    sortStateRef.current = null;
    selectedRowColorsRef.current = [];
    currentPageRef.current = 1;
    setColumnFilters({});
    setColumnFilterDrafts({});
    setSortState(null);
    setSelectedRowColors([]);
    setCurrentPage(1);
    setFilterValues(defaults);
    if (!datesChanged) {
      setListQueryVersion((version) => version + 1);
    }
  };

  const handleListDateChange = (field, date) => {
    const nextValue = toLocalYMD(date);
    currentPageRef.current = 1;
    setCurrentPage(1);
    setFilterValues((prev) => ({ ...prev, [field]: nextValue }));
  };

  const resetListQueryState = () => {
    if (currentPageRef.current !== 1) {
      skipNextListQueryRef.current = true;
    }
    currentPageRef.current = 1;
    columnFiltersRef.current = {};
    sortStateRef.current = null;
    selectedRowColorsRef.current = [];
    columnOptionsLoadedRef.current = false;
    setCurrentPage(1);
    setColumnFilters({});
    setColumnFilterDrafts({});
    setSortState(null);
    setSelectedRowColors([]);
    setColumnOptions({});
    setTotalCount(0);
    setListDateFields(null);
  };

  const formatFilterOptionLabel = (value, heading) => {
    const text = String(value ?? "").trim();
    const lower = text.toLowerCase();
    const isStatusField =
      String(heading?.field_name || "")
        .toLowerCase()
        .includes("status") &&
      !String(heading?.field_name || "")
        .toLowerCase()
        .includes("order_confirmation");
    if (
      isVoucherPaymentList &&
      isStatusField &&
      (lower === "n/a" || lower === "")
    ) {
      return "Awaiting for Approval";
    }
    if (lower === "n/a") return "N/A (blank)";
    if (["1", "true", "yes", "y"].includes(lower)) return "Yes";
    if (["0", "false", "no", "n"].includes(lower)) return "No";
    if (text === lower) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
    return text;
  };

  const handleDateChange = async (field, value) => {
    const formattedDate = appendDummyTime(value);
    setFilterValues((prev) => ({
      ...prev,
      [field]: formattedDate,
    }));
    await fetchTableTransactionData(selectedStatus, {
      ...filterValues,
      [field]: formattedDate,
    });
  };

  const [filterValues, setFilterValues] = useState(getDefaultDateRange());

  const dropdownRef = useOutsideClick(() => {
    setIsDropdownOpen(false);
  });

  const handleStatusSelect = async (status) => {
    setSelectedStatus(status);
    setIsDropdownOpen(false);
    await fetchTableTransactionData(status);
  };

  const fetchData = async () => {
    const tableHeadingsData = await getSettingsData(tabContentCategory);
    setTableHeadings(tableHeadingsData.items);
  };

  const mapResponseToTableData = (responseData) => {
    const headings = Object.keys(responseData[0] || {}).map((key) => ({
      field_name: key,
      display_name: key.replace(/_/g, " ").toUpperCase(),
      is_internal: false,
    }));

    const rows = responseData.map((item) => {
      const formattedRow = {};
      for (const key in item) {
        formattedRow[key] = item[key];
      }
      return formattedRow;
    });

    return { headings, rows };
  };

  const getTotalCountFromResponse = (response, fallbackCount = 0) => {
    const totalCount = Number(
      response?.TotalCount ??
        response?.total_count ??
        response?.totalCount ??
        response?.count,
    );

    if (Number.isFinite(totalCount) && totalCount > 0) {
      return totalCount;
    }

    return fallbackCount;
  };

  const isEmptyListValue = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    String(value).trim().toUpperCase() === "N/A";

  const getPendingWorkOrderBomNumber = (woTranId) => {
    if (!woTranId) return null;
    return sessionStorage.getItem(`workOrderBomNumber_${woTranId}`);
  };

  const getWbomTranNoFromResponse = (response) => {
    const firstTransaction = Array.isArray(response?.TransactionList)
      ? response.TransactionList[0]
      : null;
    const firstItem = Array.isArray(response?.items) ? response.items[0] : null;
    const firstDataItem = Array.isArray(response?.data)
      ? response.data[0]
      : null;

    return [
      response?.head_data?.tran_no,
      response?.data?.head_data?.tran_no,
      firstTransaction?.head_data?.tran_no,
      firstTransaction?.tran_no,
      firstItem?.head_data?.tran_no,
      firstItem?.tran_no,
      firstDataItem?.head_data?.tran_no,
      firstDataItem?.tran_no,
      response?.tran_no,
      response?.data?.tran_no,
    ].find((value) => !isEmptyListValue(value));
  };

  const hydrateWorkOrderBomNumbers = (rows, headings = []) => {
    const hasBomNoColumn = headings.some((heading) => {
      const fieldName = String(heading?.field_name || "").toLowerCase();
      const displayName = String(heading?.display_name || "").toLowerCase();
      return (
        fieldName === "bom_no" ||
        fieldName === "bom_number" ||
        fieldName === "wbom_tran_no" ||
        displayName === "bom no"
      );
    });

    if (
      String(transactionCode || "").toUpperCase() !== "WO" ||
      !hasBomNoColumn ||
      !Array.isArray(rows) ||
      rows.length === 0
    ) {
      return rows || [];
    }

    return rows.map((row) => {
      const pendingBomNo = getPendingWorkOrderBomNumber(row?.tran_id);
      if (!isEmptyListValue(pendingBomNo)) {
        return {
          ...row,
          bom_number: pendingBomNo,
        };
      }

      const existingBomNo =
        row?.bom_number ||
        row?.bom_no ||
        row?.wbom_tran_no ||
        row?.wbom_no ||
        row?.["BOM No"];

      if (!isEmptyListValue(existingBomNo)) {
        return {
          ...row,
          bom_number: existingBomNo,
        };
      }

      return row;
    });
  };

  const fetchTableReportsData = async () => {
    try {
      const reportsDataResponse = await fetchReportsData(reportsDataQuery);

      if (reportsDataResponse.description) {
        handleErrorResponse(reportsDataResponse.description);
        displayToast(reportsDataResponse.description, "error");
      } else {
        const { headings, rows } = mapResponseToTableData(reportsDataResponse);

        setTableData({
          headings: headings,
          rows: rows,
          filters: reportsDataQuery?.filter_data || [],
        });
      }
    } catch (error) {}
  };

  const fetchTableTransactionData = async (
    status = selectedStatus,
    dateRange = filterValues,
    skipCache = false,
  ) => {
    if (!transactionCode) {
      return;
    }

    if (!skipCache && tab !== "approval" && !isChildTransactionListPage) {
      const cacheKey = getCacheKey(
        tab,
        category,
        transactionCode,
        null,
        null,
        currentPage,
      );
      const cachedData = getCachedDataSync(cacheKey);
      if (cachedData) {
        setTableData(cachedData.tableData);
        setTotalPages(cachedData.totalPages);
        setFilteredData(cachedData.tableData.rows || []);
        if (cachedData.tableData?.headings?.length > 0) {
          setTableHeadings(cachedData.tableData.headings);
        }
        return;
      }
    }

    try {
      let tableDataResponse;
      const requestPage = tab === "approval" ? 1 : currentPageRef.current;
      const requestPageSize = pageSize;

      if (tab === "approval") {
        const data = await fetchApprovalTransactionDetails({
          transactionCode,
          fromDate: appendDummyTime(dateRange.fromDate.split("T")[0]),
          toDate: appendDummyTime(dateRange.toDate.split("T")[0]),
          page: requestPage,
          pageSize: requestPageSize,
          status,
        });

        tableDataResponse = data.items.map((item, index) => ({
          sl_no: index + 1,
          date: item.date,
          party_name: item.company_name,
          status: item.approve_status || "Pending",
          approved_by: item.approved_by || "N/A",
          approved_at: item.approved_at,
          tran_id: item.tran_id,
        }));

        const newTableData = {
          headings: [
            { field_name: "sl_no", display_name: "Sl No" },
            { field_name: "date", display_name: "Date" },
            { field_name: "party_name", display_name: "Party Name" },
            { field_name: "status", display_name: "Status" },
            { field_name: "approved_by", display_name: "Approved By" },
            { field_name: "approved_at", display_name: "Approved At" },
          ],
          rows: tableDataResponse,
          filters: [],
        };
        setTableData(newTableData);
        const totalPagesCount = Math.max(
          1,
          Math.ceil(
            getTotalCountFromResponse(data, tableDataResponse?.length || 0) /
              requestPageSize,
          ),
        );
        setTotalPages(totalPagesCount);
      } else {
        const fromDate = normalizeDateOnly(dateRange.fromDate);
        const toDate = normalizeDateOnly(dateRange.toDate);
        const dateFields = isChildTransactionListPage
          ? await getListDateFields()
          : [];
        const includeOptions = !columnOptionsLoadedRef.current;
        const hasQuotationStatusFilters =
          isSalesQuotationList && getSelectedQuotationStatuses().length > 0;
        const data = hasQuotationStatusFilters
          ? await fetchQuotationStatusRows({
              dateRange,
              dateFields,
              includeOptions,
              page: requestPage,
              rowsPerPage: requestPageSize,
            })
          : await fetchFilteredTransactionByCode({
              transactionCode,
              page: requestPage,
              pageSize: requestPageSize,
              filterData: { filter_data: buildColumnFilterPayload() },
              fromDate: dateFields.length ? fromDate : undefined,
              toDate: dateFields.length ? toDate : undefined,
              dateFields,
              includeOptions,
              sortBy: sortStateRef.current?.field,
              sortDir: sortStateRef.current?.direction,
            });

        if (data.description) {
          handleErrorResponse(data.description);
          displayToast(data.description, "error");
        } else {
          const headings = data.TransactionSettings?.head_view_fields || [];
          tableDataResponse = (data.TransactionList || []).map(
            (item, index) => ({
              ...item,
              sl_no: index + 1 + (requestPage - 1) * requestPageSize,
            }),
          );
          tableDataResponse = hydrateWorkOrderBomNumbers(
            tableDataResponse,
            headings,
          );
          tableDataResponse = await populateDrawingFileNames(
            tableDataResponse,
            headings,
            transactionCode,
          );

          const newTableData = {
            headings,
            rows: tableDataResponse,
            filters: data.TransactionSettings?.filter_data || [],
            transactionSettings: data.TransactionSettings,
            documentFileName:
              data.TransactionSettings?.document_file_name || "",
            colorCode: data.TransactionSettings?.color_code || "",
            customerType: data.TransactionSettings?.customer_type || "",
          };

          setTableData(newTableData);
          const total = getTotalCountFromResponse(
            data,
            tableDataResponse.length,
          );
          setTotalCount(total);
          const totalPagesCount = Math.max(
            1,
            Math.ceil(total / requestPageSize),
          );
          setTotalPages(totalPagesCount);
          if (includeOptions) {
            setColumnOptions(data.ColumnOptions || {});
            columnOptionsLoadedRef.current = true;
          }

          if (!isChildTransactionListPage) {
            const cacheKey = getCacheKey(
              tab,
              category,
              transactionCode,
              null,
              null,
              currentPage,
            );
            setCachedData(cacheKey, {
              tableData: newTableData,
              totalPages: totalPagesCount,
            });
            if (currentPage === 1 && tab && category && transactionCode) {
              sessionStorage.setItem(`cacheKey_${tab}_${category}`, cacheKey);
              sessionStorage.setItem("transactionCode", transactionCode);
            }
          }
        }
      }
    } catch (error) {}
  };

  const fetchQuotationStatusRows = async ({
    dateRange,
    dateFields,
    includeOptions,
    page,
    rowsPerPage,
  }) => {
    const selectedStatuses = getSelectedQuotationStatuses();
    const requestedRowCount = page * rowsPerPage;
    const filterPayload = buildColumnFilterPayload();
    const responses = await Promise.all(
      selectedStatuses.map((status, index) =>
        fetchFilteredTransactionByCode({
          transactionCode,
          page: 1,
          pageSize: requestedRowCount,
          filterData: {
            filter_data: [
              ...filterPayload,
              {
                field_name: "order_confirmation",
                operation: "=",
                value: status.label,
              },
            ],
          },
          fromDate: dateFields.length
            ? normalizeDateOnly(dateRange.fromDate)
            : undefined,
          toDate: dateFields.length
            ? normalizeDateOnly(dateRange.toDate)
            : undefined,
          dateFields,
          includeOptions: includeOptions && index === 0,
          sortBy: sortStateRef.current?.field,
          sortDir: sortStateRef.current?.direction,
        }),
      ),
    );
    const errorResponse = responses.find((response) => response?.description);
    if (errorResponse) return errorResponse;

    const combinedRows = responses.flatMap(
      (response) => response.TransactionList || [],
    );
    const totalCount = responses.reduce(
      (count, response) =>
        count +
        getTotalCountFromResponse(
          response,
          response.TransactionList?.length || 0,
        ),
      0,
    );
    const sortField = sortStateRef.current?.field;
    let sortedRows;

    if (sortField) {
      const sortDirection = sortStateRef.current.direction;
      sortedRows = [...combinedRows].sort((leftRow, rightRow) => {
        const leftValue = leftRow[sortField];
        const rightValue = rightRow[sortField];
        const leftDate = parseDateValue(leftValue);
        const rightDate = parseDateValue(rightValue);
        const leftNumber = Number(leftValue);
        const rightNumber = Number(rightValue);
        let comparison = 0;

        if (leftDate && rightDate) {
          comparison = leftDate - rightDate;
        } else if (
          String(leftValue ?? "") !== "" &&
          String(rightValue ?? "") !== "" &&
          Number.isFinite(leftNumber) &&
          Number.isFinite(rightNumber)
        ) {
          comparison = leftNumber - rightNumber;
        } else {
          comparison = String(leftValue ?? "").localeCompare(
            String(rightValue ?? ""),
            undefined,
            { numeric: true, sensitivity: "base" },
          );
        }

        return sortDirection === "desc" ? -comparison : comparison;
      });
    } else {
      const preferredDateFields = getPreferredDateFields();
      const dateField = resolveDateSortField(combinedRows, preferredDateFields);
      sortedRows = dateField
        ? sortByDateUsingField(combinedRows, dateField, true)
        : sortByDate(combinedRows, true, preferredDateFields);
    }

    const pageStart = (page - 1) * rowsPerPage;
    return {
      ...responses[0],
      TransactionList: sortedRows.slice(pageStart, pageStart + rowsPerPage),
      TotalCount: totalCount,
    };
  };

  const getTabFromUrl = () => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    if (pathParts.length > 0) {
      let lastPart = pathParts[pathParts.length - 1];

      if (lastPart.toLowerCase() === "customers") {
        lastPart = "customer";
      }

      return lastPart;
    }
    return "home";
  };

  const getAnalysisMenuName = () => {
    let dynamicTab = getTabFromUrl();

    const protectedCodes = ["PM", "CM", "VM"];

    if (!protectedCodes.includes(transactionCode)) {
      dynamicTab = dynamicTab.replace(/[\s-]+/g, "_");
    }

    return dynamicTab;
  };

  const fetchAnalysisReport = async (fromDate, toDate) => {
    const includeOptions = !columnOptionsLoadedRef.current;
    try {
      const response = await fetchAnalysisReportPage({
        tab: getAnalysisMenuName(),
        from_date: fromDate,
        to_date: toDate,
        page: currentPageRef.current,
        count: pageSize,
        filters: buildColumnFilterPayload(),
        includeOptions,
        sortBy: sortStateRef.current?.field,
        sortDir: sortStateRef.current?.direction,
        rowColors: selectedRowColorsRef.current,
      });

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
        setTableData((prev) => ({ ...(prev || {}), rows: [] }));
      } else {
        const rows = response.report_data || [];
        setTableData({
          headings: response.head_view_fields || [],
          rows,
          summaryData: response.summary_data || {},
          filters: [],
        });

        const total = getTotalCountFromResponse(response, rows.length);
        setTotalCount(total);
        setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
        if (includeOptions) {
          setColumnOptions(response.column_options || {});
          columnOptionsLoadedRef.current = true;
        }
      }
    } catch (error) {}
  };

  const fetchAllRowsForDownload = async () => {
    const count = Math.max(totalCount, tableData?.rows?.length || 0, 1);

    if (isAnalysisReportPage) {
      const response = await fetchAnalysisReportPage({
        tab: getAnalysisMenuName(),
        from_date: filterValues.fromDate,
        to_date: filterValues.toDate,
        page: 1,
        count,
        filters: buildColumnFilterPayload(),
        sortBy: sortStateRef.current?.field,
        sortDir: sortStateRef.current?.direction,
        rowColors: selectedRowColorsRef.current,
      });
      if (response?.description) throw new Error(response.description);
      return response.report_data || [];
    }

    if (isChildTransactionListPage && transactionCode) {
      const dateFields = await getListDateFields();
      const hasQuotationStatusFilters =
        isSalesQuotationList && getSelectedQuotationStatuses().length > 0;
      const response = hasQuotationStatusFilters
        ? await fetchQuotationStatusRows({
            dateRange: filterValues,
            dateFields,
            includeOptions: false,
            page: 1,
            rowsPerPage: count,
          })
        : await fetchFilteredTransactionByCode({
            transactionCode,
            page: 1,
            pageSize: count,
            filterData: { filter_data: buildColumnFilterPayload() },
            fromDate: dateFields.length
              ? normalizeDateOnly(filterValues.fromDate)
              : undefined,
            toDate: dateFields.length
              ? normalizeDateOnly(filterValues.toDate)
              : undefined,
            dateFields,
            sortBy: sortStateRef.current?.field,
            sortDir: sortStateRef.current?.direction,
          });
      if (response?.description) throw new Error(response.description);
      const headings =
        response.TransactionSettings?.head_view_fields ||
        tableData?.headings ||
        [];
      const rows = hydrateWorkOrderBomNumbers(
        (response.TransactionList || []).map((item, index) => ({
          ...item,
          sl_no: index + 1,
        })),
        headings,
      );
      return populateDrawingFileNames(rows, headings, transactionCode);
    }

    return tableData?.rows || [];
  };

  const fetchTableData = async (
    masterType,
    subType,
    skipCache = false,
    silent = false,
  ) => {
    if (!skipCache) {
      const cacheKey = getCacheKey(
        "master",
        category,
        null,
        masterType,
        subType,
      );
      const cachedData = getCachedDataSync(cacheKey);
      if (cachedData) {
        setTableData(cachedData.tableData);
        setTotalPages(cachedData.totalPages);
        setFilteredData(cachedData.tableData.rows || []);
        if (cachedData.tableData?.headings?.length > 0) {
          setTableHeadings(cachedData.tableData.headings);
        }
        return;
      }
    }

    try {
      if (!silent) setLoading(true);
      const formattedSubType = formatMasterType(subType);
      const data = await fetchMasterTableData(masterType, formattedSubType);
      if (data.description) {
        handleErrorResponse(data.description);
        displayToast(data.description, "error");
      } else {
        const newTableData = {
          headings: data.TransactionSettings?.head_view_fields || [],
          rows: data.TransactionList || [],
          filters:
            data.TransactionSettings?.filter_data || data.filter_data || [],
          transactionSettings: data.TransactionSettings,
        };
        setTableData(newTableData);
        const totalPagesCount = Math.ceil(
          (data.TransactionList?.length || 0) / pageSize,
        );
        setTotalPages(totalPagesCount);

        const cacheKey = generateCacheKey(
          "master",
          category,
          null,
          masterType,
          subType,
        );
        setCachedData(cacheKey, {
          tableData: newTableData,
          totalPages: totalPagesCount,
        });
        if (category && masterType && subType) {
          sessionStorage.setItem(`cacheKey_master_${category}`, cacheKey);
        }
      }
    } catch (error) {
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const forceRefresh = location.state?.forceRefresh === true;

      const sessionStorageKey = `clearCache_${tab}_${category}`;
      const shouldClearFromSession =
        sessionStorage.getItem(sessionStorageKey) === "true";
      if (shouldClearFromSession) {
        sessionStorage.removeItem(sessionStorageKey);
      }
      const shouldForceRefresh = forceRefresh || shouldClearFromSession;

      resetListQueryState();
      if (isChildTransactionListPage && transactionCode) {
        getListDateFields();
      }

      if (isAnalysisReportPage) {
        setLoading(true);
        await fetchAnalysisReport(filterValues.fromDate, filterValues.toDate);
        setLoading(false);
        return;
      }

      if (tab === "master") {
        const masterType = location.state?.transactionCode;
        const subType = location.state?.masterSubType;

        if (masterType && subType) {
          const cacheKey = getCacheKey(
            "master",
            category,
            null,
            masterType,
            subType,
          );
          const cachedData = getCachedDataSync(cacheKey);

          if (cachedData) {
            setTableData(cachedData.tableData);
            setTotalPages(cachedData.totalPages);
            setFilteredData(cachedData.tableData.rows || []);
            if (cachedData.tableData?.headings?.length > 0) {
              setTableHeadings(cachedData.tableData.headings);
            }

            fetchTableData(masterType, subType, shouldForceRefresh).catch(
              () => {},
            );
            return;
          }

          await fetchTableData(masterType, subType, shouldForceRefresh);
        } else {
          setLoading(true);
          if (category === "customer") {
            await fetchAnalysisReport(
              filterValues.fromDate,
              filterValues.toDate,
              "customer",
            );
          } else if (category === "vendor") {
            await fetchAnalysisReport(
              filterValues.fromDate,
              filterValues.toDate,
              "sourcing",
            );
          } else if (category === "product") {
            await fetchAnalysisReport(
              filterValues.fromDate,
              filterValues.toDate,
              "product-master",
            );
          }
          setLoading(false);
        }
      } else {
        if (tab === "reports") {
          setLoading(true);
          await fetchData();
          await fetchTableReportsData();
          setLoading(false);
        } else {
          const cacheKey = getCacheKey(
            tab,
            category,
            transactionCode,
            null,
            null,
            currentPage,
          );
          const cachedData = getCachedDataSync(cacheKey);

          if (cachedData && transactionCode) {
            setLoading(false);
            setTableData(cachedData.tableData);
            setTotalPages(cachedData.totalPages);
            setFilteredData(cachedData.tableData.rows || []);
            if (cachedData.tableData?.headings?.length > 0) {
              setTableHeadings(cachedData.tableData.headings);

              fetchData()
                .then(() =>
                  fetchTableTransactionData(
                    selectedStatus,
                    filterValues,
                    shouldForceRefresh,
                  ),
                )
                .catch(() => {});
            } else {
              fetchData().catch(() => {});
            }
            return;
          }

          fetchData()
            .then(() =>
              fetchTableTransactionData(
                selectedStatus,
                filterValues,
                shouldForceRefresh,
              ),
            )
            .catch(() => {});
          if (initialCachedData && transactionCode && currentPage === 1) {
            setLoading(false);
            if (initialCachedData.tableData?.headings?.length > 0) {
              setTableHeadings(initialCachedData.tableData.headings);
            } else {
              fetchData().catch(() => {});
            }
            return;
          }

          setLoading(true);
          await fetchData();
          await fetchTableTransactionData(
            selectedStatus,
            filterValues,
            shouldForceRefresh,
          );
          setLoading(false);
        }
      }
    };

    loadData();
  }, [
    tab,
    category,
    location.state?.transactionCode,
    location.state?.masterSubType,
    isAnalysisReportPage,
  ]);

  const listQueryKey = `${currentPage}|${listQueryVersion}|${filterValues.fromDate}|${filterValues.toDate}`;
  const lastListQueryKeyRef = useRef(listQueryKey);
  useEffect(() => {
    if (lastListQueryKeyRef.current === listQueryKey) return;
    lastListQueryKeyRef.current = listQueryKey;
    if (skipNextListQueryRef.current) {
      skipNextListQueryRef.current = false;
      return;
    }
    if (tab === "approval" || tab === "reports") return;
    if (!isAnalysisReportPage && !isChildTransactionListPage) return;

    const run = async () => {
      setIsListRefreshing(true);
      try {
        if (isAnalysisReportPage) {
          await fetchAnalysisReport(filterValues.fromDate, filterValues.toDate);
        } else if (transactionCode) {
          await fetchTableTransactionData(selectedStatus, filterValues, true);
        }
      } finally {
        setIsListRefreshing(false);
      }
    };

    run();
  }, [listQueryKey]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("resizing");
    };
  }, []);

  const getColumnWidthKey = () => {
    if (transactionCode) {
      return transactionCode;
    }

    const dynamicTab = getTabFromUrl();
    return `analysis_${dynamicTab}`;
  };

  useEffect(() => {
    widthsLoadedRef.current = false;
    setColumnWidths({});
  }, [getColumnWidthKey()]);

  useEffect(() => {
    const widthKey = getColumnWidthKey();
    if (
      widthKey &&
      tableData.headings &&
      tableData.headings.length > 0 &&
      !widthsLoadedRef.current
    ) {
      const savedWidths = localStorage.getItem(`column_widths_${widthKey}`);
      if (savedWidths) {
        try {
          const parsedWidths = JSON.parse(savedWidths);
          setColumnWidths(parsedWidths);
          widthsLoadedRef.current = true;
        } catch (error) {
          console.error("Error parsing saved column widths:", error);
        }
      } else {
        widthsLoadedRef.current = true;
      }
    }
  }, [getColumnWidthKey(), tableData.headings]);

  const [filteredData, setFilteredData] = useState(() => {
    return initialCachedData?.tableData?.rows || [];
  });
  const preserveRefreshOrderRef = useRef(null);

  const getRefreshIdentityField = (rows = []) => {
    if (rows.length === 0) return null;

    const candidateFields = Object.keys(rows[0] || {}).filter((field) => {
      const normalizedField = field.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (/^(sl|serial)(no|number)?$/.test(normalizedField)) return false;

      const values = rows.map((row) => row?.[field]);
      return (
        values.every(
          (value) => value !== undefined && value !== null && value !== "",
        ) && new Set(values.map(String)).size === rows.length
      );
    });

    return (
      candidateFields.find((field) => /(^|_)(id|no|number)$/i.test(field)) ||
      candidateFields[0] ||
      null
    );
  };

  const getRowIdentity = (row, identityField) => {
    const value = identityField ? row?.[identityField] : null;
    return value !== undefined && value !== null && value !== ""
      ? String(value)
      : null;
  };

  const compareRefreshOrder = (a, b) => {
    const refreshOrder = preserveRefreshOrderRef.current;
    if (!refreshOrder) return 0;

    const positionA = refreshOrder.positions.get(
      getRowIdentity(a, refreshOrder.identityField),
    );
    const positionB = refreshOrder.positions.get(
      getRowIdentity(b, refreshOrder.identityField),
    );
    const hasPositionA = positionA !== undefined;
    const hasPositionB = positionB !== undefined;

    if (hasPositionA && hasPositionB) return positionA - positionB;
    if (hasPositionA !== hasPositionB) return hasPositionA ? 1 : -1;
    return 0;
  };

  const parseDateValue = (dateStr) => {
    if (!dateStr) return null;
    const str = String(dateStr).trim();

    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const date = new Date(str);
      if (!isNaN(date.getTime())) return date;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
      const [month, day, year] = str.split("/");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthRegex = /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/;
    const monthMatch = str.match(monthRegex);
    if (monthMatch) {
      const monthStr = monthMatch[1];
      let monthIndex = monthNames.findIndex((m) =>
        m.toLowerCase().startsWith(monthStr.toLowerCase()),
      );
      if (monthIndex !== -1) {
        const date = new Date(
          parseInt(monthMatch[3]),
          monthIndex,
          parseInt(monthMatch[2]),
        );
        if (!isNaN(date.getTime())) return date;
      }
    }

    const date = new Date(str);
    if (!isNaN(date.getTime())) return date;

    return null;
  };

  const getDateFieldName = (row, preferredFields = []) => {
    const normalizeKey = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const dateFields = [
      ...preferredFields,
      ...getPreferredDateFields(),
      "mailDate",
      "createdDate",
    ];

    const isDateLikeKey = (key) => {
      const lower = String(key || "").toLowerCase();
      return (
        lower.includes("date") || lower.includes("_dt") || lower.endsWith("dt")
      );
    };

    const headingDateFields = (tableData?.headings || [])
      .filter((h) => {
        const field = String(h?.field_name || "").toLowerCase();
        const display = String(h?.display_name || "").toLowerCase();
        return (
          isDateLikeKey(field) ||
          display.includes("date") ||
          display.includes("dt")
        );
      })
      .map((h) => h?.field_name)
      .filter(Boolean);

    const parseableDateKeys = Object.keys(row || {}).filter((key) => {
      if (!isDateLikeKey(key) && !headingDateFields.includes(key)) return false;
      return Boolean(parseDateValue(row[key]));
    });

    for (const preferredField of dateFields) {
      const preferredNorm = normalizeKey(preferredField);
      const matchedKey = parseableDateKeys.find(
        (key) => normalizeKey(key) === preferredNorm,
      );
      if (matchedKey) return matchedKey;
    }

    return parseableDateKeys[0] || null;
  };

  const sortByDate = (rows, descending = false, preferredDateFields = []) => {
    return [...rows].sort((a, b) => {
      if (preserveRefreshOrderRef.current) {
        return compareRefreshOrder(a, b);
      }

      const dateField = getDateFieldName(a, preferredDateFields);
      if (!dateField) return 0;
      const dateA = parseDateValue(a[dateField]);
      const dateB = parseDateValue(b[dateField]);
      if (dateA && dateB) {
        const result = dateA - dateB;
        if (result !== 0) return descending ? -result : result;
        return compareRefreshOrder(a, b);
      }
      if (dateA) return descending ? 1 : -1;
      if (dateB) return descending ? -1 : 1;
      return compareRefreshOrder(a, b);
    });
  };

  const resolveDateSortField = (rows = [], preferredDateFields = []) => {
    const normalizeKey = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const allRowKeys = [
      ...new Set(rows.flatMap((row) => Object.keys(row || {}))),
    ];

    for (const preferredField of preferredDateFields) {
      const preferredNorm = normalizeKey(preferredField);
      const matchedKey = allRowKeys.find(
        (key) => normalizeKey(key) === preferredNorm,
      );
      if (!matchedKey) continue;

      const hasParseableValue = rows.some((row) =>
        Boolean(parseDateValue(row?.[matchedKey])),
      );
      if (hasParseableValue) return matchedKey;
    }

    return null;
  };

  const sortByDateUsingField = (rows, fieldName, descending = false) => {
    return [...rows].sort((a, b) => {
      if (preserveRefreshOrderRef.current) {
        return compareRefreshOrder(a, b);
      }

      const dateA = parseDateValue(a?.[fieldName]);
      const dateB = parseDateValue(b?.[fieldName]);
      if (dateA && dateB) {
        const result = dateA - dateB;
        if (result !== 0) return descending ? -result : result;
        return compareRefreshOrder(a, b);
      }
      if (dateA) return descending ? 1 : -1;
      if (dateB) return descending ? -1 : 1;
      return compareRefreshOrder(a, b);
    });
  };

  useEffect(() => {
    if (tableData.rows && tableData.rows.length > 0) {
      if (sortStateRef.current) {
        setFilteredData(tableData.rows);
        preserveRefreshOrderRef.current = null;
        return;
      }
      const shouldSortDescending =
        isAnalysisReportPage ||
        isChildTransactionListPage ||
        (Boolean(category) && !isEnquiryListPage);
      const preferredDateFields = isDrawingStatusPage
        ? getDrawingStatusDateFields()
        : isChildTransactionListPage
          ? getPreferredDateFields()
          : [];
      const fixedSortField = resolveDateSortField(
        tableData.rows,
        preferredDateFields,
      );
      setFilteredData(
        fixedSortField
          ? sortByDateUsingField(
              tableData.rows,
              fixedSortField,
              shouldSortDescending,
            )
          : sortByDate(
              tableData.rows,
              shouldSortDescending,
              preferredDateFields,
            ),
      );
      preserveRefreshOrderRef.current = null;
    } else {
      setFilteredData([]);
      preserveRefreshOrderRef.current = null;
    }
  }, [tableData.rows, category]);

  const fetchProductData = async () => {
    try {
      const response = await fetchVendorCategories();
      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        const categories = (response.items || response.data || response).map(
          (item) => item.category,
        );
        setProducts(categories);
      }
    } catch (error) {
      displayToast("Failed to fetch products", "error");
    }
  };

  const handleAddNewItem = () => {
    if (!rights.can_add) {
      displayToast(
        `You doesn't have permission to add new ${category}`,
        "error",
      );
      return;
    }

    if (tab === "master") {
      setIsMasterDataModalOpen(true);
      let component;
      if (transactionCode === "CM") {
        component = "CustomerMasterDataModal";
      } else if (transactionCode === "VM") {
        component = "VendorMasterDataModal";
      } else {
        component = "MasterDataModal";
      }

      const subType = location.state?.masterSubType;
      navigate(`/master/${formatNavigationPath(category)}/${subType}/add`, {
        state: {
          transactionCode,
          component,
          colorCode: tableData.colorCode || "",
        },
      });
    } else if (tab === "user") {
    } else if (category === "purchase-enquiry") {
      setIsProductModalOpen(true);
    } else if (
      category === "drawing-status" ||
      category === "dr" ||
      tableData?.transactionSettings?.type === "master"
    ) {
      navigate(`/${tab}/${formatNavigationPath(category)}/transactions`, {
        state: {
          transactionCode,
          isAddMode: true,
          colorCode: tableData.colorCode || "",
          transactionSettings: tableData.transactionSettings,
        },
      });
    } else if (tableData?.transactionSettings?.name === "JobCard") {
      navigate(`/${tab}/${formatNavigationPath(category)}/job-card`, {
        state: {
          transactionCode,
          isAddMode: true,
          isAutoTransactionNo:
            tableData.transactionSettings?.is_auto_transaction_no,
          colorCode: tableData.colorCode || "",
          backDatedEntry:
            tableData.transactionSettings.is_back_dated_entry_allowed,
          pickupMandatory: tableData.transactionSettings.is_pick_up_mandatory,
        },
      });
    } else {
      fetchCustomerOrVendorData();
      setIsModalOpen(true);
    }
  };

  const normalizeDropdownItems = (items = []) => {
    return (items || []).map((item) => {
      if (typeof item === "string") return { display_name: item };
      if (item?.display_name) return item;
      const name = item?.sub_category || item?.company_name || "";
      return { ...item, display_name: name };
    });
  };

  const fetchSubcategoryData = async (product) => {
    try {
      const response = await fetchSubcategoriesByCategory(product);
      if (response?.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
        setSubcategories([]);
        return;
      }
      setSubcategories(normalizeDropdownItems(response?.items || []));
    } catch (error) {
      displayToast("Failed to fetch subcategories", "error");
      setSubcategories([]);
    }
  };

  const fetchVendorsBySubcategoryData = async (subcategory) => {
    try {
      const response = await fetchVendorsBySubcategory(subcategory);
      if (response?.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
        setCompanies([]);
        return;
      }
      setCompanies(normalizeDropdownItems(response?.items || []));
    } catch (error) {
      displayToast("Failed to fetch supplier data", "error");
      setCompanies([]);
    }
  };

  const handleProductSelect = async (product) => {
    const productName = product?.label || product;
    setIsProductModalOpen(false);
    setSelectedProductCategory(productName || "");

    if (tableData?.customerType === "vendor") {
      if (!productName) {
        displayToast("Product not selected", "error");
        return;
      }
      await fetchSubcategoryData(productName);
      setIsSubcategoryModalOpen(true);
      return;
    }

    fetchCustomerOrVendorData(productName);
    setIsModalOpen(true);
  };

  const handleSubcategorySelect = async (subcategoryOption) => {
    const subcategoryName =
      subcategoryOption?.display_name ||
      subcategoryOption?.sub_category ||
      subcategoryOption;

    setIsSubcategoryModalOpen(false);

    if (!subcategoryName) {
      displayToast("Subcategory not selected", "error");
      return;
    }

    await fetchVendorsBySubcategoryData(subcategoryName);
    setIsModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setIsProductModalOpen(false);
  };

  const handleCloseSubcategoryModal = () => {
    setIsSubcategoryModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsMasterDataModalOpen(false);
    setIsModalOpen(false);
  };

  const handleRowClick = (rowData) => {
    if (!rights.can_view) {
      displayToast(`You doesn't have permission to view ${category}`, "error");
      return;
    }

    if (tab === "master") {
      let component;
      if (transactionCode === "CM") {
        component = "CustomerMasterDataModal";
      } else if (transactionCode === "VM") {
        component = "VendorMasterDataModal";
      } else {
        component = "MasterDataModal";
      }

      const subType = location.state?.masterSubType;
      navigate(`/master/${formatNavigationPath(category)}/${subType}/view`, {
        state: {
          transactionCode,
          transId: rowData.tran_id,
          component,
          colorCode: tableData.colorCode || "",
        },
      });
    }
  };

  const getTranCodeFromRow = (row = {}) => {
    const knownPrefixes = [
      "SE",
      "SQ",
      "SO",
      "WO",
      "DC",
      "PO",
      "PE",
      "PQ",
      "PD",
      "ST",
      "JC",
      "MR",
      "PI",
      "SI",
      "PT",
      "GRN",
      "JQ",
      "JI",
      "JE",
      "JO",
      "JR",
      "REG",
      "EC",
      "RQC",
      "QCG",
    ];

    let matchedFieldName = "";
    let matchedCode = "";

    Object.entries(row || {}).forEach(([key, value]) => {
      if (matchedCode) return;
      if (value == null) return;
      const str = String(value).trim();
      const m = str.match(/^([A-Za-z]{1,4})[\/\-]?/);
      if (m) {
        const prefix = m[1].toUpperCase();
        if (knownPrefixes.includes(prefix)) {
          matchedCode = prefix === "PO" ? "SO" : prefix;
          matchedFieldName = key;
        }
      }
    });
    if (!matchedCode) {
      const lcKeys = Object.keys(row || {}).map((k) => k.toLowerCase());
      const findKey = (substr) => lcKeys.find((k) => k.includes(substr));
      if (findKey("quotation") || findKey("sq ") || findKey("sq_"))
        matchedCode = "SQ";
      else if (findKey("enq")) matchedCode = "SE";
      else if (findKey("so ") || findKey("so_") || findKey("sales order"))
        matchedCode = "SO";
      else if (findKey("work order") || findKey("wo ") || findKey("wo_"))
        matchedCode = "WO";
      else if (findKey("dc")) matchedCode = "DC";
      else if (
        findKey("purchase order") ||
        findKey("po_") ||
        findKey(" po no") ||
        findKey("po no")
      )
        matchedCode = "SO";
      matchedFieldName =
        lcKeys.find((k) => k.includes("_no") || k.endsWith(" no")) ||
        matchedFieldName;
    }
    if (!matchedCode) {
      const hasSqPattern = Object.values(row || {}).some((value) => {
        const str = String(value ?? "").trim();
        return /^SQ[\/\-].*\d+$/.test(str);
      });
      if (hasSqPattern) {
        matchedCode = "SQ";
        const sqField = Object.entries(row || {}).find(([key, value]) => {
          const str = String(value ?? "").trim();
          return /^SQ[\/\-].*\d+$/.test(str);
        });
        if (sqField) {
          matchedFieldName = sqField[0];
        }
      }
    }

    return { code: matchedCode, fieldName: matchedFieldName };
  };

  const handleReportRowClick = async (row) => {
    try {
      const { code: tranCode, fieldName } = getTranCodeFromRow(row);
      const isQuotationReport = Array.isArray(tableData?.headings)
        ? tableData.headings.some((h) => {
            const name = String(h?.field_name || "").toLowerCase();
            const label = String(h?.display_name || "").toLowerCase();
            return (
              name.includes("quotation") ||
              name.startsWith("sq_") ||
              name.startsWith("jq_") ||
              label.includes("quotation")
            );
          })
        : false;
      const isEnquiryReport = Array.isArray(tableData?.headings)
        ? tableData.headings.some((h) => {
            const name = String(h?.field_name || "").toLowerCase();
            const label = String(h?.display_name || "").toLowerCase();
            return (
              name.includes("enquiry") ||
              name.startsWith("se_") ||
              name.startsWith("je_") ||
              label.includes("enquiry")
            );
          })
        : false;
      const reportsMeta =
        (location?.state && location.state.reportsDataQuery) ||
        reportsDataQuery ||
        {};
      const reportsMetaString = Object.values(reportsMeta)
        .filter((v) => typeof v === "string")
        .join(" ")
        .toLowerCase();
      const metaSaysQuotation =
        /quotation/.test(reportsMetaString) ||
        /\bSQ\b/.test(reportsMetaString) ||
        /sales quotation register/.test(reportsMetaString);
      const metaSaysEnquiry =
        /enquiry/.test(reportsMetaString) ||
        /\bSE\b/.test(reportsMetaString) ||
        /sales enquiry register/.test(reportsMetaString);
      const metaSaysEngineering =
        window.location.pathname.toLowerCase().includes("engineering") ||
        (reportsMetaString.includes("engineering") &&
          !reportsMetaString.includes("production") &&
          !reportsMetaString.includes("planning") &&
          !reportsMetaString.includes("jobcard") &&
          !reportsMetaString.includes("jobwork") &&
          !reportsMetaString.includes("job"));

      const isEstimatedCostingReport =
        reportsMetaString.includes("estimated costing") ||
        reportsMetaString.includes("estimated cost") ||
        reportsMetaString.includes("costing") ||
        (metaSaysEngineering &&
          !reportsMetaString.includes("drawing status") &&
          !reportsMetaString.includes("drawing") &&
          !reportsMetaString.includes("inspection input") &&
          !reportsMetaString.includes("inspection") &&
          !reportsMetaString.includes("list of document") &&
          !reportsMetaString.includes("document"));
      const isSQSalesQuotationRegister =
        /sales quotation register/.test(reportsMetaString) ||
        (reportsMetaString.includes("quotation") &&
          reportsMetaString.includes("register"));
      const isCustomerPOReport =
        /customer po|customer purchase order|purchase order register|production.*planning/.test(
          reportsMetaString,
        ) ||
        /customer.*po/.test(reportsMetaString) ||
        /production.*planning/.test(window.location.pathname.toLowerCase());
      const isJobworkContext =
        /job\s*work|jobwork|\bJQ\b|\bJE\b|\bJR\b|jobwork enquiry register|jobwork quotation register|jobwork return register/.test(
          reportsMetaString,
        ) || /^J/.test(tranCode || "");
      const isJobcardReport =
        reportsMetaString.includes("jobcard") ||
        window.location.pathname.toLowerCase().includes("jobcard");
      const isJobworkOrderReport =
        reportsMetaString.includes("jobwork") ||
        window.location.pathname.toLowerCase().includes("jobwork");
      if (!tranCode) {
        displayToast("Unable to determine transaction from row", "error");
        return;
      }
      let mappedTranCode =
        tranCode === "REG" ? "EC" : tranCode === "RQC" ? "QCG" : tranCode;
      if (
        isEstimatedCostingReport &&
        !isJobcardReport &&
        !isJobworkOrderReport
      ) {
        mappedTranCode = "EC";
      } else if (isSQSalesQuotationRegister) {
        mappedTranCode = isJobworkContext ? "JQ" : "SQ";
      } else if (isCustomerPOReport) {
        if (
          tranCode === "JO" ||
          tranCode === "JC" ||
          tranCode === "WO" ||
          tranCode === "DC" ||
          tranCode === "PO"
        ) {
          mappedTranCode = tranCode;
        } else {
          mappedTranCode = "SO";
        }
      } else if (metaSaysQuotation || isQuotationReport) {
        mappedTranCode = isJobworkContext ? "JQ" : "SQ";
      } else if (metaSaysEnquiry || isEnquiryReport) {
        mappedTranCode = isJobworkContext ? "JE" : "SE";
      } else {
      }

      if (
        isEstimatedCostingReport &&
        !isJobcardReport &&
        !isJobworkOrderReport
      ) {
        let transactionId = row?.tran_id;

        if (!transactionId) {
          if (
            row?.["Costing No"] &&
            row["Costing No"] !== "N/A" &&
            row["Costing No"] !== null
          ) {
            try {
              const ecByCostingNo = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "costing_no",
                      operation: "=",
                      value: row["Costing No"],
                    },
                  ],
                },
              });
              transactionId = ecByCostingNo?.TransactionList?.[0]?.tran_id;
            } catch (error) {}
          }

          if (
            !transactionId &&
            row?.["Enq No"] &&
            row["Enq No"] !== "N/A" &&
            row["Enq No"] !== ""
          ) {
            try {
              const ecByEnqNo = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "enquiry_no",
                      operation: "=",
                      value: row["Enq No"],
                    },
                  ],
                },
              });
              transactionId = ecByEnqNo?.TransactionList?.[0]?.tran_id;
            } catch (error) {}
          }

          if (!transactionId && row?.["Created At"]) {
            try {
              const ecByCreatedAt = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "created_at",
                      operation: "=",
                      value: row["Created At"],
                    },
                  ],
                },
              });
              transactionId = ecByCreatedAt?.TransactionList?.[0]?.tran_id;
            } catch (error) {}
          }

          if (!transactionId && row?.["Date"]) {
            try {
              const ecByDate = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    { field_name: "date", operation: "=", value: row["Date"] },
                  ],
                },
              });
              transactionId = ecByDate?.TransactionList?.[0]?.tran_id;
            } catch (error) {}
          }
        }

        if (!transactionId) {
          displayToast("Unable to find transaction ID for this row", "error");
          return;
        }

        try {
          const response = await fetchTransactionByCodeAndId(
            "EC",
            transactionId,
          );

          if (response?.description) {
            handleErrorResponse(response.description);
            displayToast(response.description, "error");
            return;
          }

          navigate(
            `/${tab}/${formatNavigationPath(category)}/transactions/invoice`,
            {
              state: {
                formData: {
                  headDetails: response.head_data || {},
                  itemDetails: response.item_data || [],
                  companyData: response.company_data || {},
                  rowData: [],
                  transactionCode: "EC",
                },
                selectedReportName: location.state?.selectedReportName,
              },
            },
          );
        } catch (error) {
          displayToast("Failed to load transaction details", "error");
        }
        return;
      }

      let tranId = row?.tran_id;

      if (tranId && (isQuotationReport || isSQSalesQuotationRegister)) {
        const primaryCode = isJobworkContext ? "JQ" : "SQ";
        try {
          const verifyResponse = await fetchFilteredTransactionByCode({
            transactionCode: primaryCode,
            page: 1,
            pageSize: 1,
            filterData: {
              filter_data: [
                { field_name: "tran_id", operation: "=", value: tranId },
              ],
            },
          });
          if (!verifyResponse?.TransactionList?.[0]) {
            tranId = undefined;
          } else {
            mappedTranCode = primaryCode;
          }
        } catch (error) {
          tranId = undefined;
        }
      }

      if (!tranId) {
        const extractRef = () => {
          if (
            isQuotationReport ||
            isSQSalesQuotationRegister ||
            metaSaysQuotation
          ) {
            const quotationFields = [
              "Tran No",
              "tran_no",
              "transaction_no",
              "quotation_no",
              "sq_no",
            ];
            for (const field of quotationFields) {
              const val = row[field];
              if (val && String(val).trim()) {
                return String(val).trim();
              }
            }
          }

          const preferKey =
            fieldName ||
            ("tran_no" in (row || {})
              ? "tran_no"
              : Object.keys(row || {}).find((k) =>
                  k.toLowerCase().includes("_no"),
                ) || "tran_no");
          const preferVal = row[preferKey];
          if (preferVal) return String(preferVal).trim();

          const commonTranFields = [
            "tran_no",
            "transaction_no",
            "quotation_no",
            "enquiry_no",
            "sq_no",
            "se_no",
            "so_no",
            "po_no",
            "costing_no",
            "estimation_no",
          ];
          for (const field of commonTranFields) {
            const val = row[field];
            if (val && String(val).trim()) {
              return String(val).trim();
            }
          }
          if (isEstimatedCostingReport) {
            for (const [key, value] of Object.entries(row || {})) {
              const str = String(value ?? "").trim();
              if (
                str &&
                str !== "N/A" &&
                str !== "n/a" &&
                str !== "" &&
                (str.includes("/") ||
                  /^[A-Za-z]{2,}[\/\-]\d+/.test(str) ||
                  /^\d+$/.test(str) ||
                  str.includes("SO") ||
                  str.includes("SQ") ||
                  str.includes("SE") ||
                  str.includes("EC") ||
                  str.includes("REG") ||
                  str.includes("PO") ||
                  str.includes("DC"))
              ) {
                return str;
              }
            }
          }
          if (isCustomerPOReport) {
            for (const [key, value] of Object.entries(row || {})) {
              const str = String(value ?? "").trim();
              if (
                str &&
                (str.includes("PO") ||
                  str.includes("po") ||
                  /^#?\s*[A-Za-z]*-?PO-?\d+/.test(str) ||
                  /^\d{4,}$/.test(str) ||
                  /^[A-Za-z]{2,}-?\d+/.test(str))
              ) {
                return str;
              }
            }
          }

          for (const v of Object.values(row || {})) {
            const str = String(v ?? "").trim();
            if (/^[A-Za-z]{1,4}[\/-].*\d+$/.test(str)) return str;
          }
          return "";
        };

        const reference = extractRef();
        if (!reference) {
          if (isEstimatedCostingReport) {
            const anyEcTransaction = await fetchFilteredTransactionByCode({
              transactionCode: "EC",
              page: 1,
              pageSize: 1,
              filterData: { filter_data: [] },
            });
            tranId = anyEcTransaction?.TransactionList?.[0]?.tran_id;
            if (tranId) {
              mappedTranCode = "EC";
            } else {
              return displayToast("No EC transactions found", "error");
            }
          } else {
            return displayToast("Transaction reference missing", "error");
          }
        } else {
          const tryFetch = async (operation, value) => {
            const resp = await fetchFilteredTransactionByCode({
              transactionCode: mappedTranCode,
              page: 1,
              pageSize: 1,
              filterData: {
                filter_data: [{ field_name: "tran_no", operation, value }],
              },
            });
            return resp?.TransactionList?.[0]?.tran_id;
          };

          tranId = await tryFetch("=", reference);
          if (!tranId) tranId = await tryFetch("%", reference);
          if (!tranId) {
            const tail = reference.split("/").pop();
            if (tail) tranId = await tryFetch("%", tail);
          }
          if (!tranId && (isQuotationReport || isSQSalesQuotationRegister)) {
            const primaryCode = isJobworkContext ? "JQ" : "SQ";

            if (row?.tran_id && !tranId) {
              const verifyTranId = await fetchFilteredTransactionByCode({
                transactionCode: primaryCode,
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "tran_id",
                      operation: "=",
                      value: row.tran_id,
                    },
                  ],
                },
              });
              if (verifyTranId?.TransactionList?.[0]) {
                tranId = row.tran_id;
                mappedTranCode = primaryCode;
              }
            }

            if (!tranId) {
              const sqByTranNo = await fetchFilteredTransactionByCode({
                transactionCode: primaryCode,
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    { field_name: "tran_no", operation: "=", value: reference },
                  ],
                },
              });
              tranId = sqByTranNo?.TransactionList?.[0]?.tran_id || tranId;

              if (!tranId) {
                const sqByTranNoLike = await fetchFilteredTransactionByCode({
                  transactionCode: primaryCode,
                  page: 1,
                  pageSize: 1,
                  filterData: {
                    filter_data: [
                      {
                        field_name: "tran_no",
                        operation: "%",
                        value: reference,
                      },
                    ],
                  },
                });
                tranId =
                  sqByTranNoLike?.TransactionList?.[0]?.tran_id || tranId;
              }
              if (!tranId) {
                const tail = reference.split("/").pop();
                if (tail) {
                  const sqByTail = await fetchFilteredTransactionByCode({
                    transactionCode: primaryCode,
                    page: 1,
                    pageSize: 1,
                    filterData: {
                      filter_data: [
                        { field_name: "tran_no", operation: "%", value: tail },
                      ],
                    },
                  });
                  tranId = sqByTail?.TransactionList?.[0]?.tran_id || tranId;
                }
              }

              if (
                !tranId &&
                (isSQSalesQuotationRegister ||
                  (isQuotationReport && !isSQSalesQuotationRegister))
              ) {
                const sqByEnqExact = await fetchFilteredTransactionByCode({
                  transactionCode: primaryCode,
                  page: 1,
                  pageSize: 1,
                  filterData: {
                    filter_data: [
                      {
                        field_name: "enquiry_no",
                        operation: "=",
                        value: reference,
                      },
                    ],
                  },
                });
                tranId = sqByEnqExact?.TransactionList?.[0]?.tran_id || tranId;
                if (!tranId) {
                  const tail = reference.split("/").pop();
                  const sqByEnqLike = await fetchFilteredTransactionByCode({
                    transactionCode: primaryCode,
                    page: 1,
                    pageSize: 1,
                    filterData: {
                      filter_data: [
                        {
                          field_name: "enquiry_no",
                          operation: "%",
                          value: tail || reference,
                        },
                      ],
                    },
                  });
                  tranId = sqByEnqLike?.TransactionList?.[0]?.tran_id || tranId;
                }
              }

              mappedTranCode = primaryCode;
            }
          }

          if (!tranId && isCustomerPOReport) {
            const soByTranNo = await fetchFilteredTransactionByCode({
              transactionCode: "SO",
              page: 1,
              pageSize: 1,
              filterData: {
                filter_data: [
                  { field_name: "tran_no", operation: "=", value: reference },
                ],
              },
            });
            tranId = soByTranNo?.TransactionList?.[0]?.tran_id || tranId;

            if (!tranId) {
              const soByTranNoLike = await fetchFilteredTransactionByCode({
                transactionCode: "SO",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    { field_name: "tran_no", operation: "%", value: reference },
                  ],
                },
              });
              tranId = soByTranNoLike?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const soByPoNo = await fetchFilteredTransactionByCode({
                transactionCode: "SO",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "customer_po_no",
                      operation: "=",
                      value: reference,
                    },
                  ],
                },
              });
              tranId = soByPoNo?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const soByPoNoLike = await fetchFilteredTransactionByCode({
                transactionCode: "SO",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "customer_po_no",
                      operation: "%",
                      value: reference,
                    },
                  ],
                },
              });
              tranId = soByPoNoLike?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const soByPoNoAlt = await fetchFilteredTransactionByCode({
                transactionCode: "SO",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    { field_name: "po_no", operation: "=", value: reference },
                  ],
                },
              });
              tranId = soByPoNoAlt?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const soByPoNoAltLike = await fetchFilteredTransactionByCode({
                transactionCode: "SO",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    { field_name: "po_no", operation: "%", value: reference },
                  ],
                },
              });
              tranId = soByPoNoAltLike?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const tail = reference.split("/").pop();
              if (tail) {
                const soByTail = await fetchFilteredTransactionByCode({
                  transactionCode: "SO",
                  page: 1,
                  pageSize: 1,
                  filterData: {
                    filter_data: [
                      { field_name: "tran_no", operation: "%", value: tail },
                    ],
                  },
                });
                tranId = soByTail?.TransactionList?.[0]?.tran_id || tranId;
              }
            }

            if (!tranId && reference.startsWith("SQ/")) {
              const soBySqRef = await fetchFilteredTransactionByCode({
                transactionCode: "SO",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "quotation_no",
                      operation: "=",
                      value: reference,
                    },
                  ],
                },
              });
              tranId = soBySqRef?.TransactionList?.[0]?.tran_id || tranId;

              if (!tranId) {
                const tail = reference.split("/").pop();
                const soBySqRefLike = await fetchFilteredTransactionByCode({
                  transactionCode: "SO",
                  page: 1,
                  pageSize: 1,
                  filterData: {
                    filter_data: [
                      {
                        field_name: "quotation_no",
                        operation: "%",
                        value: tail || reference,
                      },
                    ],
                  },
                });
                tranId = soBySqRefLike?.TransactionList?.[0]?.tran_id || tranId;
              }
            }

            mappedTranCode = "SO";
          }

          if (
            !tranId &&
            isEstimatedCostingReport &&
            !isJobcardReport &&
            !isJobworkOrderReport
          ) {
            const ecFilterData = [
              { field_name: "tran_no", operation: "=", value: reference },
              { field_name: "tran_no", operation: "%", value: reference },
              { field_name: "costing_no", operation: "=", value: reference },
              { field_name: "costing_no", operation: "%", value: reference },
              { field_name: "enquiry_no", operation: "=", value: reference },
              { field_name: "enquiry_no", operation: "%", value: reference },
            ];

            if (/^\d+$/.test(reference)) {
              ecFilterData.push({
                field_name: "tran_id",
                operation: "=",
                value: reference,
              });
            }

            const ecSearchResult = await fetchFilteredTransactionByCode({
              transactionCode: "EC",
              page: 1,
              pageSize: 1,
              filterData: {
                filter_data: ecFilterData,
              },
            });
            tranId = ecSearchResult?.TransactionList?.[0]?.tran_id || tranId;

            if (!tranId && reference.startsWith("SO/")) {
              const ecBySoRef = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    { field_name: "so_no", operation: "=", value: reference },
                  ],
                },
              });
              tranId = ecBySoRef?.TransactionList?.[0]?.tran_id || tranId;

              if (!tranId) {
                const tail = reference.split("/").pop();
                const ecBySoRefLike = await fetchFilteredTransactionByCode({
                  transactionCode: "EC",
                  page: 1,
                  pageSize: 1,
                  filterData: {
                    filter_data: [
                      {
                        field_name: "so_no",
                        operation: "%",
                        value: tail || reference,
                      },
                    ],
                  },
                });
                tranId = ecBySoRefLike?.TransactionList?.[0]?.tran_id || tranId;
              }
            }

            if (!tranId) {
              const ecByCustomerPo = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "customer_po_no",
                      operation: "=",
                      value: reference,
                    },
                  ],
                },
              });
              tranId = ecByCustomerPo?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const ecByCustomerPoLike = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "customer_po_no",
                      operation: "%",
                      value: reference,
                    },
                  ],
                },
              });
              tranId =
                ecByCustomerPoLike?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const ecByEstimationNo = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "estimation_no",
                      operation: "=",
                      value: reference,
                    },
                  ],
                },
              });
              tranId =
                ecByEstimationNo?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const ecByEstimationNoLike = await fetchFilteredTransactionByCode(
                {
                  transactionCode: "EC",
                  page: 1,
                  pageSize: 1,
                  filterData: {
                    filter_data: [
                      {
                        field_name: "estimation_no",
                        operation: "%",
                        value: reference,
                      },
                    ],
                  },
                },
              );
              tranId =
                ecByEstimationNoLike?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const ecByProjectName = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: {
                  filter_data: [
                    {
                      field_name: "project_name",
                      operation: "%",
                      value: reference,
                    },
                  ],
                },
              });
              tranId = ecByProjectName?.TransactionList?.[0]?.tran_id || tranId;
            }

            if (!tranId) {
              const tail = reference.split("/").pop();
              if (tail) {
                const ecByTail = await fetchFilteredTransactionByCode({
                  transactionCode: "EC",
                  page: 1,
                  pageSize: 1,
                  filterData: {
                    filter_data: [
                      { field_name: "tran_no", operation: "%", value: tail },
                    ],
                  },
                });
                tranId = ecByTail?.TransactionList?.[0]?.tran_id || tranId;
              }
            }

            if (!tranId) {
              const anyEcTransaction = await fetchFilteredTransactionByCode({
                transactionCode: "EC",
                page: 1,
                pageSize: 1,
                filterData: { filter_data: [] },
              });
              tranId =
                anyEcTransaction?.TransactionList?.[0]?.tran_id || tranId;
            }

            mappedTranCode = "EC";
          }
        }

        if (!tranId) {
          if (
            isEstimatedCostingReport &&
            !isJobcardReport &&
            !isJobworkOrderReport
          ) {
            if (row?.tran_id) {
              tranId = row.tran_id;
              mappedTranCode = "EC";
            }
          }
        }

        if (!tranId) {
          displayToast(
            `Transaction not found for the selected row. Reference: ${reference}, Transaction Code: ${mappedTranCode}`,
            "error",
          );
          return;
        }
      }

      const response = await fetchTransactionByCodeAndId(
        mappedTranCode,
        tranId,
      );
      if (response?.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
        return;
      }

      navigate(
        `/${tab}/${formatNavigationPath(category)}/transactions/invoice`,
        {
          state: {
            formData: {
              headDetails: response.head_data || {},
              itemDetails: response.item_data || [],
              companyData: response.company_data || {},
              rowData: [],
              transactionCode: mappedTranCode,
            },
            selectedReportName: location.state?.selectedReportName,
          },
        },
      );
    } catch (err) {
      displayToast(err?.message || "Failed to open invoice", "error");
    }
  };

  const handleSubmitModal = (formData) => {};

  const [woPicker, setWoPicker] = useState(null);

  const [expandedProductRows, setExpandedProductRows] = useState({});
  const [productBomCache, setProductBomCache] = useState({});

  const toggleProductBom = async (item) => {
    const tranId = item?.tran_id;
    if (!tranId) return;

    const willExpand = !expandedProductRows[tranId];
    setExpandedProductRows((prev) => ({ ...prev, [tranId]: willExpand }));

    if (!willExpand || productBomCache[tranId]) return;

    setProductBomCache((prev) => ({ ...prev, [tranId]: { loading: true } }));
    try {
      const response = await fetchBomByProduct(tranId);
      if (response?.head_data) {
        setProductBomCache((prev) => ({
          ...prev,
          [tranId]: { loading: false, data: response },
        }));
      } else {
        setProductBomCache((prev) => ({
          ...prev,
          [tranId]: {
            loading: false,
            error: response?.description || "No BOM created yet",
          },
        }));
      }
    } catch (error) {
      setProductBomCache((prev) => ({
        ...prev,
        [tranId]: { loading: false, error: "No BOM created yet" },
      }));
    }
  };

  const handleViewFullBom = (bomHead, productItem) => {
    if (!bomHead?.wo_tran_id) return;
    navigate(
      `/master/${formatNavigationPath(category)}/${masterSubTypeValue}/bom`,
      {
        state: {
          transactionCode: "WO",
          transactionId: Number(bomHead.wo_tran_id),
          primaryDetails: { bom: String(bomHead.tran_id) },
          productData: {
            tran_id: bomHead.product_id,
            name:
              productItem?.name ||
              productItem?.product_name ||
              productItem?.Product_Name ||
              "",
            description:
              productItem?.description || productItem?.product_desc || "",
          },
        },
      },
    );
  };

  const buildMappedRecord = (mappings, source) => {
    const mapped = {};
    (mappings || []).forEach((mapping) => {
      const sourceField = mapping.field_name;
      const targetField = mapping.copy_to_field_name;
      if (source?.[sourceField] !== undefined) {
        mapped[targetField] = source[sourceField];
      }
    });
    return mapped;
  };

  const handleBatchCreateWorkOrders = async () => {
    if (!woPicker || woPicker.creating) return;
    const selectedItems = woPicker.items.filter((_, i) => woPicker.selected[i]);
    if (selectedItems.length === 0) {
      displayToast("Select at least one product", "warning");
      return;
    }

    const { childCode, childSettings, mappingConfig, coHeadData, companyData } =
      woPicker;

    if (!childSettings?.is_auto_transaction_no) {
      displayToast(
        "Work Order numbering must be automatic for batch creation",
        "error",
      );
      return;
    }

    setWoPicker((prev) => ({ ...prev, creating: true, progress: 0 }));

    const created = [];
    let failedItem = null;
    let failedMessage = "";

    for (let i = 0; i < selectedItems.length; i += 1) {
      const sourceItem = selectedItems[i];
      setWoPicker((prev) =>
        prev ? { ...prev, progress: i + 1, total: selectedItems.length } : prev,
      );
      try {
        const head = buildMappedRecord(mappingConfig?.head_fields, coHeadData);
        const itemRow = buildMappedRecord(
          mappingConfig?.item_fields,
          sourceItem,
        );

        if (
          sourceItem.product_id !== undefined &&
          itemRow.product_id === undefined
        ) {
          itemRow.product_id = sourceItem.product_id;
        }
        if (!itemRow.product_name) {
          itemRow.product_name =
            sourceItem.product_name || sourceItem.Product_Name || "";
        }

        const rate = parseFloat(itemRow.rate ?? itemRow.unit_rate ?? 0) || 0;
        const quantity = parseFloat(itemRow.quantity ?? itemRow.qty ?? 0) || 0;
        const amount = rate * quantity;
        itemRow.amount = amount.toFixed(2);

        const tranNoResponse = await fetchTransactionNo(childCode);
        const tranNo = tranNoResponse?.TransactionNumber;
        if (!tranNo) {
          throw new Error(
            tranNoResponse?.description || "Failed to fetch WO number",
          );
        }

        head.tran_no = tranNo;
        head.tran_code = childCode;
        if (!head.date) {
          head.date = toLocalYMD(new Date());
        }
        head.total_quantity = quantity;
        head.remaining_quantity = quantity;
        head.net_amt = amount.toFixed(2);
        head.gross_amt = String(sourceItem.gross_amount ?? amount.toFixed(2));

        const payload = {
          tran_id: null,
          company_data: companyData || {},
          head_data: head,
          item_data: [itemRow],
          ...(woPicker.sourceTranCode && woPicker.sourceTranId
            ? {
                source_tran_code: woPicker.sourceTranCode,
                source_tran_id: Number(woPicker.sourceTranId),
              }
            : {}),
        };

        const result = await insertTransactionByCode(payload, childCode);
        if (result?.description) {
          throw new Error(result.description);
        }
        created.push(tranNo);
      } catch (error) {
        failedItem =
          sourceItem.product_name || sourceItem.Product_Name || `item ${i + 1}`;
        failedMessage = error?.message || "Insert failed";
        break;
      }
    }

    if (created.length > 0) {
      displayToast(
        `${created.length} Work Order${created.length > 1 ? "s" : ""} created (${created.join(", ")})`,
        "success",
      );
    }
    if (failedItem) {
      displayToast(`Failed for ${failedItem}: ${failedMessage}`, "error");
    }

    setWoPicker(null);
    if (created.length > 0) {
      try {
        await fetchTableTransactionData(selectedStatus, undefined, true);
      } catch (error) {}
    }
  };

  const handleCreateTransaction = async (
    rowData,
    transactionCode,
    transactionName,
    transactionSettings,
    baseTransactionCode,
  ) => {
    if (
      rowData?.is_latest !== undefined &&
      rowData?.is_latest !== null &&
      Number(rowData.is_latest) === 0
    ) {
      displayToast(
        "This quotation is superseded \u2014 open the latest revision instead",
        "warning",
      );
      return;
    }
    const formattedTransactionName = transactionName
      .toLowerCase()
      .replace(/\s+/g, "-");

    const dynamicTransactionSettings =
      await fetchTransactionSettingsByCode(transactionCode);

    const isMasterType =
      formattedTransactionName === "drawing-status" ||
      formattedTransactionName === "dr" ||
      dynamicTransactionSettings?.type === "master";

    if (isMasterType) {
      navigate(
        `/${tab}/${formatNavigationPath(formattedTransactionName)}/transactions`,
        {
          state: {
            transactionCode: rowData.transactionCode || transactionCode,
            isAddMode: true,
            colorCode: dynamicTransactionSettings.colorCode || "",
            transactionSettings: {
              ...dynamicTransactionSettings,
              type: "master",
            },
            prefillData: {
              workorder_no:
                rowData.tran_no || rowData.transaction_no || rowData.tran_id,
            },
            sourceTranCode: baseTransactionCode,
            sourceTranId: rowData.tran_id,
            sourceCategory: category,
          },
        },
      );
      return;
    }

    const response = await fetchTransactionByCodeAndId(
      baseTransactionCode,
      rowData.tran_id,
    );

    const childCode = String(rowData.transactionCode || transactionCode || "")
      .trim()
      .toUpperCase();

    if (childCode === "WO") {
      const items = response.item_data || [];
      if (items.length === 0) {
        displayToast("This customer order has no products", "warning");
        return;
      }
      const mappingConfig = (
        transactionSettings?.dynamic_transaction || []
      ).find((config) => config?.tran_code === childCode);
      setWoPicker({
        items,
        selected: items.map(() => true),
        childCode,
        childSettings: dynamicTransactionSettings,
        mappingConfig,
        coHeadData: response.head_data || {},
        companyData: response.company_data || {},
        sourceTranCode: baseTransactionCode,
        sourceTranId: rowData.tran_id,
        creating: false,
        progress: 0,
        total: 0,
      });
      return;
    }

    navigate(
      `/${tab}/${formatNavigationPath(formattedTransactionName)}/transactions`,
      {
        state: {
          rowData,
          transactionCode: rowData.transactionCode || transactionCode,

          company: response.company_data || {},
          dynamicHeadData: response.head_data || {},
          dynamicItemData: response.item_data || [],
          dynamicTransaction: transactionSettings?.dynamic_transaction || null,
          backDatedEntry:
            dynamicTransactionSettings.is_back_dated_entry_allowed,
          pickupMandatory: dynamicTransactionSettings.is_pick_up_mandatory,
          isAutoTransactionNo:
            dynamicTransactionSettings.is_auto_transaction_no,
          isAddMode: true,
          colorCode: dynamicTransactionSettings.colorCode || "",
          customerType: dynamicTransactionSettings.customerType,
          sourceTranCode: baseTransactionCode,
          sourceTranId: rowData.tran_id,
          sourceCategory: category,
        },
      },
    );
  };

  const fetchCustomerOrVendorData = async (product) => {
    const type = tableData?.customerType === "vendor" ? "vendor" : "customer";
    let response;
    try {
      if (product) {
        response = await fetchCustomerOrVendorwithProduct(product);
      } else {
        response = await fetchCustomerOrVendor({ type });
      }

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        setCompanies(normalizeDropdownItems(response.items || []));
      }
    } catch (error) {
      displayToast("Failed to fetch company data", "error");
    }
  };

  const handleCompanySelect = (company) => {
    navigate(`/${tab}/${formatNavigationPath(category)}/transactions`, {
      state: {
        transactionCode: transactionCode,
        company,
        backDatedEntry:
          tableData.transactionSettings.is_back_dated_entry_allowed,
        pickupMandatory: tableData.transactionSettings.is_pick_up_mandatory,
        isAutoTransactionNo:
          tableData.transactionSettings.is_auto_transaction_no,
        colorCode: tableData.colorCode || "",
        customerType: tableData.customerType,
      },
    });
    setIsModalOpen(false);
  };

  const handleBackClick = () => {
    const isDetailsRoute = window.location.pathname.endsWith("/details");

    if (tab && category && isDetailsRoute) {
      navigate(`/${tab}/${category}`);
      return;
    }

    if (tab && category) {
      navigate(`/${tab}`);
      return;
    }

    if (tab && !category) {
      navigate("/");
      return;
    }

    navigate(-1);
  };

  const updateColumnWidthsInAPI = async (widths) => {
    try {
      if (
        !tableData.headings ||
        tableData.headings.length === 0 ||
        !transactionCode
      ) {
        return;
      }

      const flexSettings = tableData.headings.map((heading) => ({
        field_name: heading.field_name,
        is_internal: heading.is_internal,
        display_name: heading.display_name,
        flex: widths[heading.field_name]
          ? Math.round(widths[heading.field_name] / 170)
          : (heading.flex ?? 1),
      }));

      const settingsData = {
        head_view_fields: flexSettings,
      };

      await updateTransactionSettings(settingsData);
    } catch (error) {
      console.error("Failed to update column settings in API:", error);
      displayToast("Failed to save column settings", "error");
    }
  };

  const getColumnWidth = (heading) => {
    if (columnWidths[heading.field_name]) {
      return columnWidths[heading.field_name];
    }

    if (["party_name", "company_name"].includes(heading.field_name)) {
      return 300;
    }
    if (heading.flex === 0) {
      return 85;
    }
    return (heading.flex ?? 1) * 170;
  };

  const handleMouseDown = (e, fieldName) => {
    e.preventDefault();
    setResizingColumn(fieldName);
    document.body.classList.add("resizing");

    const startX = e.clientX;
    const startWidth = getColumnWidth({ field_name: fieldName });

    const handleMouseMove = (e) => {
      const newWidth = Math.max(5, startWidth + (e.clientX - startX));
      const updatedWidths = {
        ...columnWidths,
        [fieldName]: newWidth,
      };
      setColumnWidths(updatedWidths);

      const widthKey = getColumnWidthKey();
      if (widthKey) {
        localStorage.setItem(
          `column_widths_${widthKey}`,
          JSON.stringify(updatedWidths),
        );
      }
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
      document.body.classList.remove("resizing");
      updateColumnWidthsInAPI(columnWidths);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleColumnDoubleClick = (fieldName) => {
    setColumnWidths((prev) => {
      const newWidths = { ...prev };
      delete newWidths[fieldName];
      const widthKey = getColumnWidthKey();
      if (widthKey) {
        localStorage.setItem(
          `column_widths_${widthKey}`,
          JSON.stringify(newWidths),
        );
      }
      setTimeout(() => updateColumnWidthsInAPI(newWidths), 0);
      return newWidths;
    });
  };

  const autoRefreshList = async () => {
    if (loading || isListRefreshing) return;
    if (
      isModalOpen ||
      isProductModalOpen ||
      isMasterDataModalOpen ||
      isSubcategoryModalOpen
    )
      return;

    const identityField = getRefreshIdentityField(filteredData || []);
    preserveRefreshOrderRef.current = identityField
      ? {
          identityField,
          positions: new Map(
            (filteredData || []).map((row, index) => [
              getRowIdentity(row, identityField),
              index,
            ]),
          ),
        }
      : null;

    if (tab === "reports") {
      await fetchTableReportsData();
      return;
    }

    if (isAnalysisReportPage) {
      await fetchAnalysisReport(filterValues.fromDate, filterValues.toDate);
      return;
    }

    if (tab === "master") {
      const masterType = location.state?.transactionCode;
      const subType = location.state?.masterSubType;
      if (masterType && subType) {
        await fetchTableData(masterType, subType, true, true);
      } else if (["customer", "vendor", "product"].includes(category)) {
        await fetchAnalysisReport(filterValues.fromDate, filterValues.toDate);
      }
      return;
    }

    if (transactionCode) {
      await fetchTableTransactionData(selectedStatus, filterValues, true);
    }
  };

  useAutoRefresh(autoRefreshList);

  const isReturningWithCache =
    initialCachedData?.tableData?.rows?.length > 0 &&
    !location.pathname.endsWith("/details");

  const visibleRows = Array.isArray(filteredData)
    ? isMasterSubtypeList
      ? filteredData.filter((row) =>
          Object.entries(columnFilters).every(([fieldName, filter]) => {
            const expectedValue = String(filter.value ?? "")
              .trim()
              .toLowerCase();
            if (!expectedValue) return true;

            const rowFieldName = Object.keys(row || {}).find(
              (key) => key.toLowerCase() === fieldName.toLowerCase(),
            );
            const cellValue = rowFieldName ? row[rowFieldName] : undefined;

            if (expectedValue === "n/a") {
              return isEmptyListValue(cellValue);
            }
            if (filter.operation === "date=") {
              const originalDate = String(cellValue ?? "");
              const parsedDate = parseDateValue(cellValue);
              const normalizedDate = /^\d{4}-\d{2}-\d{2}/.test(originalDate)
                ? originalDate.slice(0, 10)
                : parsedDate
                  ? toLocalYMD(parsedDate)
                  : "";
              return normalizedDate === filter.value;
            }

            const actualValue = String(cellValue ?? "")
              .trim()
              .toLowerCase();
            return filter.operation === "="
              ? actualValue === expectedValue
              : actualValue.includes(expectedValue);
          }),
        )
      : filteredData
    : [];
  const canReorderColumns = isAnalysisReportPage || isChildTransactionListPage;

  const baseVisibleHeadings = [
    ...(tableData?.headings || []).filter(
      (heading) =>
        !heading.is_internal &&
        !["created_at", "updated_at"].includes(heading.field_name),
    ),
    ...(tableData?.headings || []).filter(
      (heading) =>
        !heading.is_internal &&
        ["created_at", "updated_at"].includes(heading.field_name),
    ),
  ];
  const columnOrderKey = `${tab || "unknown"}:${category || "analysis"}:${transactionCode || ""}`;
  const savedColumnOrder = columnOrderByPage[columnOrderKey] || [];
  const orderedVisibleHeadings = canReorderColumns
    ? [
        ...savedColumnOrder
          .map((fieldName) =>
            baseVisibleHeadings.find(
              (heading) => heading.field_name === fieldName,
            ),
          )
          .filter(Boolean),
        ...baseVisibleHeadings.filter(
          (heading) => !savedColumnOrder.includes(heading.field_name),
        ),
      ]
    : baseVisibleHeadings;
  const visibleBodyHeadings = canReorderColumns
    ? orderedVisibleHeadings
    : orderedVisibleHeadings.filter(
        (heading) => !["created_at", "updated_at"].includes(heading.field_name),
      );
  const trailingBodyHeadings = canReorderColumns
    ? []
    : orderedVisibleHeadings.filter((heading) =>
        ["created_at", "updated_at"].includes(heading.field_name),
      );
  const hasActionColumn =
    Array.isArray(tableData?.transactionSettings?.dynamic_transaction) &&
    tableData.transactionSettings.dynamic_transaction.filter(
      (tran) => tran !== null && tran !== undefined,
    ).length > 0;
  const supportsColumnFilters = canReorderColumns || isMasterSubtypeList;
  const supportsColumnSorting = canReorderColumns;
  const showColumnFilterRow =
    supportsColumnFilters && orderedVisibleHeadings.length > 0;
  const activeColumnFilterCount =
    Object.keys(columnFilters).length + selectedRowColors.length;
  const pageStartIndex = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const pageEndIndex =
    totalCount > 0
      ? Math.min(pageStartIndex + visibleRows.length - 1, totalCount)
      : 0;
  const paginationSummaryText =
    totalCount > 0
      ? `Showing ${pageStartIndex}\u2013${pageEndIndex} of ${totalCount}`
      : "No records";

  const renderHeadingLabel = (displayName) => {
    if (!displayName || displayName.length <= 16) return displayName;
    const words = displayName.split(" ");
    const chunks = [];
    for (let i = 0; i < words.length; i++) {
      const current = words[i];
      if (current === "&" && i > 0 && i < words.length - 1) {
        chunks[chunks.length - 1] = `${chunks[chunks.length - 1]} & ${
          words[i + 1]
        }`;
        i += 1;
      } else {
        chunks.push(current);
      }
    }
    const finalChunks =
      chunks.length > 2 ? [chunks[0], chunks.slice(1).join(" ")] : chunks;
    return finalChunks.map((chunk, i) => (
      <span key={i}>
        {chunk}
        <br />
      </span>
    ));
  };

  const handleColumnDragStart = (event, fieldName) => {
    if (!canReorderColumns) return;
    if (event.target.closest(".column-resizer, button")) {
      event.preventDefault();
      return;
    }

    setDraggingColumnField(fieldName);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", fieldName);

    const table = event.currentTarget.closest("table");
    const columnCells = Array.from(
      table?.querySelectorAll("[data-field]") || [],
    ).filter((cell) => cell.dataset.field === fieldName);
    const dragPreview = document.createElement("div");
    dragPreview.className = "analysis-column-drag-preview";
    dragPreview.style.width = `${event.currentTarget.offsetWidth}px`;

    columnCells.forEach((cell, index) => {
      const previewCell = document.createElement("div");
      previewCell.className =
        index === 0
          ? "analysis-column-drag-preview__heading"
          : "analysis-column-drag-preview__cell";
      previewCell.textContent = cell.textContent.trim() || " ";
      dragPreview.appendChild(previewCell);
    });

    document.body.appendChild(dragPreview);
    event.dataTransfer.setDragImage(dragPreview, 20, 20);
    window.setTimeout(() => dragPreview.remove(), 0);
  };

  const handleColumnDrop = (event, targetFieldName) => {
    if (!canReorderColumns) return;
    event.preventDefault();

    const sourceFieldName =
      event.dataTransfer.getData("text/plain") || draggingColumnField;
    if (!sourceFieldName || sourceFieldName === targetFieldName) return;

    const reorderedHeadings = [...orderedVisibleHeadings];
    const sourceIndex = reorderedHeadings.findIndex(
      (heading) => heading.field_name === sourceFieldName,
    );
    const targetIndex = reorderedHeadings.findIndex(
      (heading) => heading.field_name === targetFieldName,
    );
    if (sourceIndex < 0 || targetIndex < 0) return;

    const [movedHeading] = reorderedHeadings.splice(sourceIndex, 1);
    reorderedHeadings.splice(targetIndex, 0, movedHeading);
    setColumnOrderByPage((previous) => ({
      ...previous,
      [columnOrderKey]: reorderedHeadings.map((heading) => heading.field_name),
    }));
    setDraggingColumnField(null);
  };

  const renderHeadingCell = (heading, index) => {
    const fieldName = heading.field_name;
    const label = heading.display_name || fieldName;
    const isSorted = supportsColumnSorting && sortState?.field === fieldName;
    const sortDirection = isSorted ? sortState.direction : null;
    const sortHint = !supportsColumnSorting
      ? undefined
      : !isSorted
        ? `Double-click the heading or use the arrow to sort by ${label} (A → Z)`
        : sortDirection === "asc"
          ? `Sorted A → Z. Double-click or use the arrow for Z → A`
          : `Sorted Z → A. Double-click or use the arrow to clear sorting`;

    return (
      <th
        key={index}
        data-field={fieldName}
        draggable={canReorderColumns}
        onDragStart={(event) => handleColumnDragStart(event, fieldName)}
        onDragOver={(event) => {
          if (canReorderColumns) event.preventDefault();
        }}
        onDrop={(event) => handleColumnDrop(event, fieldName)}
        onDragEnd={() => setDraggingColumnField(null)}
        className={`table-data resizable-column${
          supportsColumnSorting ? " sortable-heading" : ""
        }${isSorted ? ` sortable-heading--${sortDirection}` : ""}${
          canReorderColumns ? " analysis-column-draggable" : ""
        }${draggingColumnField === fieldName ? " analysis-column-dragging" : ""}`}
        style={{
          width: `${getColumnWidth(heading)}px`,
          minWidth: `${getColumnWidth(heading)}px`,
          maxWidth: `${getColumnWidth(heading)}px`,
          position: "relative",
        }}
        aria-sort={
          isSorted
            ? sortDirection === "asc"
              ? "ascending"
              : "descending"
            : undefined
        }
        onDoubleClick={() =>
          supportsColumnSorting
            ? toggleColumnSort(fieldName)
            : handleColumnDoubleClick(fieldName)
        }
      >
        <div className="heading-cell">
          <span className="heading-cell__label">
            {renderHeadingLabel(heading.display_name)}
          </span>
          {supportsColumnSorting && (
            <span className="heading-cell__tools">
              <span
                className="heading-cell__info"
                tabIndex={0}
                aria-label={sortHint}
                data-tooltip={sortHint}
              >
                i
              </span>
              <button
                type="button"
                className={`heading-sort-btn${
                  isSorted ? ` heading-sort-btn--${sortDirection}` : ""
                }`}
                title={sortHint}
                aria-label={sortHint}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleColumnSort(fieldName);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                <ArrowDown size={13} strokeWidth={2.4} />
              </button>
            </span>
          )}
        </div>
        <div
          className="column-resizer"
          onMouseDown={(e) => handleMouseDown(e, fieldName)}
          onDoubleClick={(e) => {
            e.stopPropagation();
            handleColumnDoubleClick(fieldName);
          }}
          title="Drag to resize • Double-click to reset width"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "4px",
            height: "100%",
            cursor: "col-resize",
            backgroundColor:
              resizingColumn === fieldName ? "var(--primary)" : "transparent",
            zIndex: 10,
          }}
        />
      </th>
    );
  };

  const marketingLegendItems = [
    {
      key: "quote",
      swatch: "quote",
      label: "Quote Created, Order & WO Not Created",
    },
    {
      key: "order",
      swatch: "order",
      label: "Quote & Order Created, WO Not Created",
    },
    { key: "all", swatch: "all", label: "All Status Created" },
    {
      key: "not-possible",
      swatch: "not-possible",
      label: "Feasibility Not Possible",
    },
  ];

  const deliveryLegendItems = [
    {
      key: "delivery-delivered",
      swatch: "delivery-delivered",
      label: "Delivered",
    },
    {
      key: "delivery-warning",
      swatch: "delivery-warning",
      label: "1 to 4 Days Left",
    },
    {
      key: "delivery-caution",
      swatch: "delivery-caution",
      label: "5 to 10 Days Left",
    },
    {
      key: "delivery-safe",
      swatch: "delivery-safe",
      label: "More Than 10 Days",
    },
    {
      key: "delivery-delayed",
      swatch: "delivery-delayed",
      label: "Delivery Delayed",
    },
    {
      key: "delivery-no-date",
      swatch: "delivery-no-date",
      label: "No Delivery Date",
    },
  ];

  const renderColorLegend = (title, items, ariaLabel) => (
    <div className="marketing-status-legend" aria-label={ariaLabel}>
      <div className="marketing-status-legend__title">
        {title}
        <span className="marketing-status-legend__hint">click to filter</span>
      </div>
      <div className="marketing-status-legend__items" role="group">
        {items.map((item) => {
          const isSelected = selectedRowColors.includes(item.key);
          return (
            <button
              type="button"
              key={item.key}
              className={`marketing-status-legend__item marketing-status-legend__item--button${
                isSelected ? " is-selected" : ""
              }`}
              aria-pressed={isSelected}
              title={
                isSelected
                  ? `Showing "${item.label}" rows — click to remove this filter`
                  : `Show only "${item.label}" rows (multi-select)`
              }
              onClick={() => toggleRowColorFilter(item.key)}
            >
              <span
                className={`marketing-status-legend__swatch marketing-status-legend__swatch--${item.swatch}`}
                aria-hidden="true"
              />
              <span className="marketing-status-legend__label">
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderColumnFilterCell = (heading) => {
    const fieldName = heading.field_name;
    const options = columnOptions[fieldName];
    const appliedValue = columnFilters[fieldName]?.value ?? "";
    const draftValue = columnFilterDrafts[fieldName] ?? "";
    const width = getColumnWidth(heading);
    const isActive = appliedValue !== "";
    const isDateField = isDateLikeHeading(heading);
    const isDropdown =
      !isDateField && Array.isArray(options) && options.length > 0;
    const label = heading.display_name || fieldName;

    return (
      <th
        key={`filter-${fieldName}`}
        data-field={fieldName}
        className={`table-data column-filter-cell${
          isActive ? " column-filter-cell--active" : ""
        }${draggingColumnField === fieldName ? " analysis-column-dragging" : ""}`}
        style={{
          width: `${width}px`,
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
        }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {isDateField ? (
          <div className="column-filter column-filter-date">
            <DatePicker
              selected={
                appliedValue ? new Date(`${appliedValue}T00:00:00`) : null
              }
              onChange={(date) =>
                applyColumnFilter(fieldName, toLocalYMD(date), "date=")
              }
              dateFormat="dd/MM/yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={26}
              dropdownMode="scroll"
              customInput={
                <ColumnDateFilterInput
                  placeholder="Select date"
                  title={`Filter ${label} by date`}
                />
              }
              popperClassName="analysis-datepicker-popper"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              popperModifiers={CALENDAR_MODIFIERS}
              portalId="datepicker-portal"
            />
            {isActive && (
              <button
                type="button"
                className="column-filter-btn column-filter-btn--clear"
                title="Clear"
                aria-label={`Clear ${label} filter`}
                onClick={() => clearColumnFilter(fieldName)}
              >
                &#10005;
              </button>
            )}
          </div>
        ) : isDropdown ? (
          <select
            className="column-filter-select"
            value={appliedValue}
            onChange={(e) => applyColumnFilter(fieldName, e.target.value, "=")}
            title={`Filter ${label}`}
          >
            <option value="">All</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {formatFilterOptionLabel(option, heading)}
              </option>
            ))}
          </select>
        ) : (
          <div className="column-filter">
            <input
              type="text"
              className="column-filter-input"
              placeholder="Search"
              value={draftValue}
              onChange={(e) =>
                setColumnFilterDrafts((prev) => ({
                  ...prev,
                  [fieldName]: e.target.value,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyColumnFilter(fieldName, draftValue);
                } else if (e.key === "Escape") {
                  clearColumnFilter(fieldName);
                }
              }}
              title={`Search ${label} (type N/A to find blank rows)`}
            />
            {(isActive || draftValue) && (
              <button
                type="button"
                className="column-filter-btn column-filter-btn--clear"
                title="Clear"
                aria-label={`Clear ${heading.display_name || fieldName} filter`}
                onClick={() => clearColumnFilter(fieldName)}
              >
                &#10005;
              </button>
            )}
            <button
              type="button"
              className="column-filter-btn column-filter-btn--go"
              title="Search"
              aria-label={`Search ${heading.display_name || fieldName}`}
              onClick={() => applyColumnFilter(fieldName, draftValue)}
            >
              <img src={Search} alt="" />
            </button>
          </div>
        )}
      </th>
    );
  };

  const isWorkOrderList =
    isChildTransactionListPage &&
    String(transactionCode).toUpperCase() === "WO";
  const listDownloadProps = {
    tableData,
    tableHeadings,
    fileName: `${category}_data`,
    primaryColor: "#2563eb",
    fullPageDownload: false,
    fetchAllRows: isChildTransactionListPage
      ? fetchAllRowsForDownload
      : undefined,
    refNo: buildDocumentRefNo(tableData?.transactionSettings, transactionCode),
    revNo: tableData?.transactionSettings?.rev_no,
    revDate: tableData?.transactionSettings?.rev_date,
  };

  const resetFiltersButton = (
    <button
      type="button"
      className="list-reset-button"
      onClick={handleResetAllFilters}
      title="Reset all filters and dates"
    >
      <RotateCcw size={15} strokeWidth={2.2} />
      Reset
      {activeColumnFilterCount > 0 && (
        <span className="list-reset-count">{activeColumnFilterCount}</span>
      )}
    </button>
  );

  return (
    <>
      {loading && !isReturningWithCache ? (
        <Loader />
      ) : (
        <div className="tab-content-detail-wrapper">
          <div className="tab-detail-header">
            <div className="tab-detail-table-heading-container">
              {tab && category && (
                <button className="back-button" onClick={handleBackClick}>
                  <ArrowLeft size={25} />
                </button>
              )}
              {!category &&
                tab !== "master" &&
                tab !== "reports" &&
                tab !== "approval" && (
                  <span className="tab-detail-heading-icon">
                    <ClipboardList size={20} />
                  </span>
                )}
              <h5 className="tab-detail-table-heading">
                {tab === "master"
                  ? category
                    ? `${capitalizeFirstLetterOfEachWord(category)}`
                    : ""
                  : tab === "reports" && location.state?.selectedReportName
                    ? cleanReportName(location.state.selectedReportName)
                    : category
                      ? capitalizeFirstLetterOfEachWord(category)
                      : `${capitalizeFirstLetterOfEachWord(tab)} Analysis`}
              </h5>
              {isMasterSubtypeList && (
                <div
                  className="master-subtype-switch"
                  role="group"
                  aria-label="Master subtype"
                >
                  {masterSubTypes.map((subtype) => {
                    const isActive =
                      String(masterSubTypeValue)
                        .toLowerCase()
                        .replace(/-/g, " ") === subtype.name.toLowerCase();

                    return (
                      <button
                        key={subtype.name}
                        type="button"
                        className={`master-subtype-switch__button${
                          isActive ? " is-active" : ""
                        }`}
                        aria-pressed={isActive}
                        onClick={() =>
                          navigate(
                            `/master/${formatNavigationPath(category)}/${subtype.name
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`,
                            {
                              state: {
                                transactionCode,
                                masterSubType: subtype.name,
                                isMasterSubType: true,
                              },
                            },
                          )
                        }
                      >
                        {subtype.display_name}
                      </button>
                    );
                  })}
                </div>
              )}
              {tab !== "reports" && tab !== "approval" && category && (
                <AddNewButton
                  onClick={handleAddNewItem}
                  category={category}
                  style={{
                    cursor: rights.can_add ? "pointer" : "not-allowed",
                    opacity: rights.can_add ? 1 : 0.5,
                  }}
                />
              )}
              <div className="tab-detail-header-actions">
                {isChildTransactionListPage && resetFiltersButton}
                {tab !== "reports" &&
                  tab !== "approval" &&
                  category &&
                  (tableData?.rows?.length > 0 || isWorkOrderList) &&
                  (isWorkOrderList ? (
                    <WorkOrderSalesOrderDownload
                      listDownloadProps={listDownloadProps}
                    />
                  ) : (
                    <DownloadButton {...listDownloadProps} />
                  ))}
              </div>
            </div>
          </div>
          <div className="tab-detail-category-table">
            {tab !== "approval" &&
              !isMasterSubListPage &&
              (isChildTransactionListPage || !category) && (
                <div className="tab-detail-category-table-filter-container">
                  {category ? (
                    <div className="tab-detail-category-table-buttons">
                      <div
                        className={`tab-detail-category-table-filters${
                          isSalesQuotationList
                            ? " tab-detail-category-table-filters--quotation"
                            : ""
                        }`}
                      >
                        {isChildTransactionListPage &&
                          listDateFields?.length > 0 && (
                            <div className="tab-detail-category-filters-row">
                              <div className="date-input-wrapper">
                                <label className="filter-label">From</label>
                                <DatePicker
                                  selected={
                                    filterValues.fromDate
                                      ? new Date(filterValues.fromDate)
                                      : null
                                  }
                                  onChange={(date) =>
                                    handleListDateChange("fromDate", date)
                                  }
                                  dateFormat="dd/MM/yyyy"
                                  showMonthDropdown
                                  showYearDropdown
                                  scrollableYearDropdown
                                  yearDropdownItemNumber={26}
                                  dropdownMode="scroll"
                                  customInput={
                                    <AnalysisDateInput placeholder="From Date" />
                                  }
                                  popperClassName="analysis-datepicker-popper"
                                  popperPlacement="bottom-start"
                                  popperProps={{ strategy: "fixed" }}
                                  popperModifiers={CALENDAR_MODIFIERS}
                                  portalId="datepicker-portal"
                                />
                              </div>

                              <div className="date-input-wrapper">
                                <label className="filter-label">To</label>
                                <DatePicker
                                  selected={
                                    filterValues.toDate
                                      ? new Date(filterValues.toDate)
                                      : null
                                  }
                                  onChange={(date) =>
                                    handleListDateChange("toDate", date)
                                  }
                                  dateFormat="dd/MM/yyyy"
                                  showMonthDropdown
                                  showYearDropdown
                                  scrollableYearDropdown
                                  yearDropdownItemNumber={26}
                                  dropdownMode="scroll"
                                  customInput={
                                    <AnalysisDateInput placeholder="To Date" />
                                  }
                                  popperClassName="analysis-datepicker-popper"
                                  popperPlacement="bottom-start"
                                  popperProps={{ strategy: "fixed" }}
                                  popperModifiers={CALENDAR_MODIFIERS}
                                  portalId="datepicker-portal"
                                />
                              </div>
                            </div>
                          )}
                        {isSalesQuotationList &&
                          renderColorLegend(
                            "Quote Status Colors",
                            QUOTATION_STATUS_LEGEND_ITEMS,
                            "Quotation status colors",
                          )}
                      </div>
                    </div>
                  ) : (
                    <div className="filter-section styled-filter-section">
                      <div className="date-inputs">
                        <div className="filter-row">
                          <div className="date-input-wrapper">
                            <label className="filter-label">From</label>
                            <DatePicker
                              selected={
                                filterValues.fromDate
                                  ? new Date(filterValues.fromDate)
                                  : null
                              }
                              onChange={(date) =>
                                handleListDateChange("fromDate", date)
                              }
                              dateFormat="dd/MM/yyyy"
                              showMonthDropdown
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={26}
                              dropdownMode="scroll"
                              customInput={
                                <AnalysisDateInput placeholder="From Date" />
                              }
                              popperClassName="analysis-datepicker-popper"
                              popperPlacement="bottom-start"
                              popperProps={{ strategy: "fixed" }}
                              popperModifiers={CALENDAR_MODIFIERS}
                              portalId="datepicker-portal"
                            />
                          </div>
                          <div className="date-input-wrapper">
                            <label className="filter-label">To</label>
                            <DatePicker
                              selected={
                                filterValues.toDate
                                  ? new Date(filterValues.toDate)
                                  : null
                              }
                              onChange={(date) =>
                                handleListDateChange("toDate", date)
                              }
                              dateFormat="dd/MM/yyyy"
                              showMonthDropdown
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={26}
                              dropdownMode="scroll"
                              customInput={
                                <AnalysisDateInput placeholder="To Date" />
                              }
                              popperClassName="analysis-datepicker-popper"
                              popperPlacement="bottom-start"
                              popperProps={{ strategy: "fixed" }}
                              popperModifiers={CALENDAR_MODIFIERS}
                              portalId="datepicker-portal"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="summary-data">
                        {tableData?.summaryData &&
                          Object.entries(tableData.summaryData).map(
                            ([key, value]) => (
                              <div key={key} className="summary-item">
                                <span className="summary-label">{key}</span>
                                <span className="summary-value">{value}</span>
                              </div>
                            ),
                          )}
                      </div>
                      {tab === "marketing" &&
                        renderColorLegend(
                          "Status Colors",
                          marketingLegendItems,
                          "Marketing status colors",
                        )}
                      {isProductionAnalysisPage &&
                        renderColorLegend(
                          "Delivery Colors",
                          deliveryLegendItems,
                          "Delivery status colors",
                        )}
                    </div>
                  )}
                </div>
              )}
            {tab === "approval" && (
              <div className="approval-filters">
                <div
                  className="tab-detail-category-dropdown-container-status"
                  ref={dropdownRef}
                >
                  <div
                    className="tab-detail-category-dropdown-button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {selectedStatus
                      ? selectedStatus.charAt(0).toUpperCase() +
                        selectedStatus.slice(1)
                      : "Status"}
                    <span className="dropdown-arrow">
                      <img src={DropDown} alt="dropdown" />
                    </span>
                  </div>

                  {isDropdownOpen && (
                    <div className="tab-detail-category-dropdown-options-status">
                      {statusOptions.map((status, index) => (
                        <div
                          key={index}
                          className="add-item-dropdown-option"
                          onClick={() => handleStatusSelect(status)}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  className="filter-section"
                  style={{ display: "flex", gap: "1rem" }}
                >
                  <div className="date-inputs">
                    <div className="date-input-wrapper">
                      <label className="filter-label">From</label>
                      <DatePicker
                        selected={
                          filterValues.fromDate
                            ? new Date(filterValues.fromDate)
                            : null
                        }
                        onChange={(date) =>
                          handleDateChange("fromDate", toLocalYMD(date))
                        }
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={26}
                        dropdownMode="scroll"
                        customInput={
                          <AnalysisDateInput placeholder="From Date" />
                        }
                        popperClassName="analysis-datepicker-popper"
                        popperPlacement="bottom-start"
                        popperProps={{ strategy: "fixed" }}
                        popperModifiers={CALENDAR_MODIFIERS}
                        portalId="datepicker-portal"
                      />
                    </div>
                    <div className="date-input-wrapper">
                      <label className="filter-label">To</label>
                      <DatePicker
                        selected={
                          filterValues.toDate
                            ? new Date(filterValues.toDate)
                            : null
                        }
                        onChange={(date) =>
                          handleDateChange("toDate", toLocalYMD(date))
                        }
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={26}
                        dropdownMode="scroll"
                        customInput={
                          <AnalysisDateInput placeholder="To Date" />
                        }
                        popperClassName="analysis-datepicker-popper"
                        popperPlacement="bottom-start"
                        popperProps={{ strategy: "fixed" }}
                        popperModifiers={CALENDAR_MODIFIERS}
                        portalId="datepicker-portal"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div
              className={`${
                category == null
                  ? "table-heading-container-alt"
                  : "table-heading-container"
              }${isListRefreshing ? " table-heading-container--refreshing" : ""}${
                supportsColumnSorting && totalPages > 1
                  ? " table-heading-container--paged"
                  : ""
              }`}
            >
              {!category &&
                tab !== "approval" &&
                tab !== "reports" &&
                tab !== "master" && (
                  <div className="table-download-bar">
                    <span className="list-pagination-summary">
                      {paginationSummaryText}
                    </span>
                    <div className="table-download-actions">
                      {resetFiltersButton}
                      <DownloadButton
                        tableData={tableData}
                        tableHeadings={tableHeadings}
                        fileName={`${tab}_data`}
                        primaryColor="#2563eb"
                        fullPageDownload={false}
                        fetchAllRows={fetchAllRowsForDownload}
                      />
                    </div>
                  </div>
                )}
              <div className="table-heading-wrapper">
                <table className="table-container custom-table-container">
                  <thead className="table-head">
                    <tr className="table-heading">
                      {isMasterProductsList && (
                        <th
                          className="table-data product-bom-chevron-col"
                          aria-label="Expand BOM"
                        />
                      )}
                      {orderedVisibleHeadings.map((heading, index) =>
                        renderHeadingCell(heading, index),
                      )}
                      {hasActionColumn && (
                        <th className="table-data action">Action</th>
                      )}
                    </tr>
                    {showColumnFilterRow && (
                      <tr className="table-filter-row">
                        {isMasterProductsList && (
                          <th className="table-data product-bom-chevron-col" />
                        )}
                        {orderedVisibleHeadings.map((heading) =>
                          renderColumnFilterCell(heading),
                        )}
                        {hasActionColumn && (
                          <th className="table-data action column-filter-cell" />
                        )}
                      </tr>
                    )}
                    <tr className="table-border">
                      <td
                        colSpan={
                          tableData?.headings?.filter(
                            (heading) =>
                              !heading.is_internal &&
                              !["created_at", "updated_at"].includes(
                                heading.field_name,
                              ),
                          ).length +
                          tableData?.headings?.filter(
                            (heading) =>
                              !heading.is_internal &&
                              ["created_at", "updated_at"].includes(
                                heading.field_name,
                              ),
                          ).length +
                          (tableData?.transactionSettings?.dynamic_transaction
                            ? 1
                            : 0) +
                          (isMasterProductsList ? 1 : 0)
                        }
                      >
                        <div className="fixed-line-bottom"></div>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length > 0 ? (
                      visibleRows.map((item, rowIndex) => {
                        let feasibilityValue = null;
                        let quoteStatusValue = null;
                        let orderStatusValue = null;
                        let woStatusValue = null;
                        let deliveryDaysValue = null;

                        tableData.headings?.forEach((heading) => {
                          const lowerFieldName =
                            heading.field_name?.toLowerCase() || "";
                          const lowerDisplayName =
                            heading.display_name?.toLowerCase() || "";
                          const cellData = item[heading.field_name];
                          if (
                            (lowerFieldName.includes("feasibility") ||
                              lowerDisplayName.includes("feasibility")) &&
                            !feasibilityValue
                          ) {
                            feasibilityValue = cellData;
                          }
                          if (
                            (lowerFieldName.includes("quote") &&
                              lowerFieldName.includes("status")) ||
                            (lowerDisplayName.includes("quote") &&
                              lowerDisplayName.includes("status")) ||
                            (isSalesQuotationList &&
                              lowerFieldName === "order_confirmation")
                          ) {
                            quoteStatusValue = cellData;
                          }
                          if (
                            (lowerFieldName.includes("order") &&
                              lowerFieldName.includes("status") &&
                              !lowerFieldName.includes("order_confirmation")) ||
                            (lowerDisplayName.includes("order") &&
                              lowerDisplayName.includes("status") &&
                              !lowerDisplayName.includes("order_confirmation"))
                          ) {
                            orderStatusValue = cellData;
                          }

                          if (
                            (lowerFieldName.includes("wo") &&
                              lowerFieldName.includes("status")) ||
                            (lowerFieldName.includes("work") &&
                              lowerFieldName.includes("order") &&
                              lowerFieldName.includes("status")) ||
                            (lowerDisplayName.includes("wo") &&
                              lowerDisplayName.includes("status")) ||
                            (lowerDisplayName.includes("work") &&
                              lowerDisplayName.includes("order") &&
                              lowerDisplayName.includes("status"))
                          ) {
                            woStatusValue = cellData;
                          }

                          if (
                            heading.field_name === "day_difference_to_delivery"
                          ) {
                            deliveryDaysValue = cellData;
                          }
                        });

                        let rowBorderColor = undefined;
                        let marketingRowClass = "";

                        if (tab === "marketing" && !isSalesQuotationList) {
                          const normalizedFeasibility = feasibilityValue
                            ?.toString()
                            .toLowerCase()
                            .trim();
                          const normalizedQuoteStatus = quoteStatusValue
                            ?.toString()
                            .toLowerCase()
                            .trim();
                          const normalizedOrderStatus = orderStatusValue
                            ?.toString()
                            .toLowerCase()
                            .trim();
                          const normalizedWoStatus = woStatusValue
                            ?.toString()
                            .toLowerCase()
                            .trim();

                          if (
                            category !== "enquiry" &&
                            normalizedFeasibility === "not possible"
                          ) {
                            marketingRowClass = "marketing-row-danger";
                            rowBorderColor = "#dc3545";
                          } else {
                            const isQuoteCreated =
                              normalizedQuoteStatus === "created";
                            const isOrderCreated =
                              normalizedOrderStatus === "created";
                            const isWoCreated =
                              normalizedWoStatus === "created";
                            const isQuoteNotCreated =
                              normalizedQuoteStatus &&
                              normalizedQuoteStatus !== "created";
                            const isOrderNotCreated =
                              normalizedOrderStatus &&
                              normalizedOrderStatus !== "created";
                            const isWoNotCreated =
                              normalizedWoStatus &&
                              normalizedWoStatus !== "created";

                            if (
                              normalizedFeasibility === "possible" &&
                              normalizedQuoteStatus === "created" &&
                              normalizedOrderStatus === "created" &&
                              normalizedWoStatus === "created"
                            ) {
                              marketingRowClass = "marketing-row-success";
                              rowBorderColor = "#28a745";
                            } else if (
                              isQuoteCreated &&
                              isOrderNotCreated &&
                              isWoNotCreated
                            ) {
                              marketingRowClass = "marketing-row-warning";
                              rowBorderColor = "#ff9933";
                            } else if (
                              isQuoteCreated &&
                              isOrderCreated &&
                              isWoNotCreated
                            ) {
                              marketingRowClass = "marketing-row-info";
                              rowBorderColor = "#1d4ed8";
                            }
                          }
                        }

                        let deliveryRowClass = "";
                        if (
                          isProductionAnalysisPage &&
                          deliveryDaysValue != null
                        ) {
                          const deliveryText = String(deliveryDaysValue).trim();
                          const deliveryUpper = deliveryText.toUpperCase();
                          const deliveryDaysNum = parseInt(deliveryText, 10);
                          const isDelivered =
                            deliveryUpper.includes("DELIVERED") &&
                            !deliveryUpper.includes("DELAYED");
                          const isWithinFiveDays =
                            !isDelivered &&
                            !isNaN(deliveryDaysNum) &&
                            deliveryDaysNum >= 1 &&
                            deliveryDaysNum <= 4;
                          const isFiveToTenDays =
                            !isDelivered &&
                            !isNaN(deliveryDaysNum) &&
                            deliveryDaysNum >= 5 &&
                            deliveryDaysNum <= 10;
                          const isMoreThanTenDays =
                            !isDelivered &&
                            !isNaN(deliveryDaysNum) &&
                            deliveryDaysNum > 10;
                          const isDelayed =
                            !isDelivered &&
                            (deliveryUpper.includes("DELAYED") ||
                              (!isNaN(deliveryDaysNum) && deliveryDaysNum < 1));

                          if (isDelivered) {
                            deliveryRowClass = "delivery-row-delivered";
                          } else if (isWithinFiveDays) {
                            deliveryRowClass = "delivery-row-warning";
                          } else if (isFiveToTenDays) {
                            deliveryRowClass = "delivery-row-caution";
                          } else if (isMoreThanTenDays) {
                            deliveryRowClass = "delivery-row-safe";
                          } else if (isDelayed) {
                            deliveryRowClass = "delivery-row-urgent";
                          }
                        }

                        return (
                          <React.Fragment key={rowIndex}>
                            <tr
                              className={`${
                                tab !== "reports" && category
                                  ? "hoverable-row"
                                  : ""
                              } ${deliveryRowClass} ${marketingRowClass}`.trim()}
                              onClick={
                                category || tab === "reports"
                                  ? () => {
                                      if (
                                        tab !== "reports" &&
                                        !rights.can_view
                                      ) {
                                        displayToast(
                                          `You dont have permission to view ${category}`,
                                          "error",
                                        );
                                        return;
                                      }

                                      if (tab === "reports") {
                                        handleReportRowClick(item);
                                      } else if (tab === "master") {
                                        handleRowClick(item);
                                      } else if (
                                        tableData?.transactionSettings?.type ===
                                        "master"
                                      ) {
                                        navigate(
                                          `/${tab}/${formatNavigationPath(
                                            category,
                                          )}/transactions`,
                                          {
                                            state: {
                                              transactionId: item.tran_id,
                                              colorCode: tableData.colorCode,
                                              transactionCode: transactionCode,
                                              transactionSettings:
                                                tableData.transactionSettings,
                                            },
                                          },
                                        );
                                      } else if (
                                        tableData?.transactionSettings?.name ===
                                        "JobCard"
                                      ) {
                                        navigate(
                                          `/${tab}/${formatNavigationPath(
                                            category,
                                          )}/job-card`,
                                          {
                                            state: {
                                              transactionCode: transactionCode,
                                              transactionId: item.tran_id,
                                              colorCode: tableData.colorCode,
                                              isAutoTransactionNo:
                                                tableData.transactionSettings
                                                  ?.is_auto_transaction_no,
                                              backDatedEntry:
                                                tableData?.transactionSettings
                                                  ?.is_back_dated_entry_allowed,
                                              pickupMandatory:
                                                tableData.transactionSettings
                                                  ?.is_pick_up_mandatory,
                                            },
                                          },
                                        );
                                      } else {
                                        navigate(
                                          `/${tab}/${category}/transactions`,
                                          {
                                            state: {
                                              transactionId: item.tran_id,
                                              colorCode: tableData.colorCode,
                                              transactionCode: transactionCode,
                                              documentFileName:
                                                tableData.documentFileName,
                                              backDatedEntry:
                                                tableData?.transactionSettings
                                                  ?.is_back_dated_entry_allowed,
                                              pickupMandatory:
                                                tableData.transactionSettings
                                                  ?.is_pick_up_mandatory,
                                              isAutoTransactionNo:
                                                tableData.transactionSettings
                                                  ?.is_auto_transaction_no,
                                              customerType:
                                                tableData?.customerType,
                                            },
                                          },
                                        );
                                      }
                                    }
                                  : undefined
                              }
                              style={{
                                cursor:
                                  tab === "reports" ||
                                  (rights.can_view && category)
                                    ? "pointer"
                                    : "not-allowed",
                              }}
                            >
                              {isMasterProductsList && (
                                <td
                                  className="table-data product-bom-chevron-col"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleProductBom(item);
                                  }}
                                >
                                  <button
                                    type="button"
                                    className={`product-bom-chevron${
                                      expandedProductRows[item.tran_id]
                                        ? " product-bom-chevron--open"
                                        : ""
                                    }`}
                                    title={
                                      expandedProductRows[item.tran_id]
                                        ? "Hide BOM materials"
                                        : "Show BOM materials"
                                    }
                                    aria-label="Toggle BOM materials"
                                  >
                                    &#9656;
                                  </button>
                                </td>
                              )}
                              {visibleBodyHeadings.map((heading, colIndex) => {
                                const cellData = (() => {
                                  const isBomNoColumn =
                                    heading.field_name === "bom_no" ||
                                    heading.field_name === "bom_number" ||
                                    heading.field_name === "wbom_tran_no" ||
                                    heading.display_name
                                      ?.toLowerCase()
                                      .trim() === "bom no";
                                  if (isBomNoColumn) {
                                    const bomNoCandidates = [
                                      getPendingWorkOrderBomNumber(
                                        item?.tran_id,
                                      ),
                                      item.bom_number,
                                      item.bom_no,
                                      item.wbom_tran_no,
                                      item.wbom_no,
                                      item["BOM No"],
                                    ];

                                    return bomNoCandidates.find(
                                      (value) => !isEmptyListValue(value),
                                    );
                                  }

                                  if (item[heading.field_name] !== undefined) {
                                    return item[heading.field_name];
                                  }
                                  const normalizeKey = (key) =>
                                    key
                                      ?.toLowerCase()
                                      .replace(/[^a-z0-9]/g, "") || "";
                                  const target = normalizeKey(
                                    heading.field_name,
                                  );
                                  const matchedKey = Object.keys(item).find(
                                    (key) => normalizeKey(key) === target,
                                  );
                                  return matchedKey
                                    ? item[matchedKey]
                                    : undefined;
                                })();
                                const lowerFieldName =
                                  heading.field_name?.toLowerCase() || "";
                                const lowerDisplayName =
                                  heading.display_name?.toLowerCase() || "";
                                const isRemarksField =
                                  lowerFieldName.includes("remark") ||
                                  lowerDisplayName.includes("remark");
                                const displayCellData =
                                  isRemarksField &&
                                  String(cellData ?? "")
                                    .trim()
                                    .toLowerCase() === "n/a"
                                    ? ""
                                    : cellData;
                                const isDateLikeField =
                                  lowerFieldName.includes("date") ||
                                  lowerFieldName.includes("time") ||
                                  lowerDisplayName.includes("date") ||
                                  lowerDisplayName.includes("time");
                                const isIsoDateValue =
                                  typeof displayCellData === "string" &&
                                  /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(
                                    displayCellData.trim(),
                                  );
                                const isDrawingField = /drawing|dwg/.test(
                                  `${lowerFieldName} ${lowerDisplayName}`,
                                );
                                const formattedCellData =
                                  (isDateLikeField || isIsoDateValue) &&
                                  displayCellData
                                    ? (() => {
                                        const formattedValue =
                                          formatDate(displayCellData);
                                        return formattedValue === "N/A"
                                          ? displayCellData
                                          : formattedValue;
                                      })()
                                    : isDrawingField
                                      ? formatFileUrlValue(displayCellData)
                                      : displayCellData;
                                const isStatusField =
                                  heading.field_name
                                    .toLowerCase()
                                    .includes("status") &&
                                  !heading.field_name
                                    .toLowerCase()
                                    .includes("order_confirmation");
                                const isVoucherPaymentStatusField =
                                  isVoucherPaymentList && isStatusField;
                                const isQuotationStatusField =
                                  (isStatusField &&
                                    (lowerFieldName.includes("quote") ||
                                      lowerDisplayName.includes("quote"))) ||
                                  (lowerFieldName.includes(
                                    "order_confirmation",
                                  ) &&
                                    (lowerDisplayName.includes(
                                      "quote status",
                                    ) ||
                                      String(heading.title ?? "")
                                        .toLowerCase()
                                        .includes("quote status")));
                                const quotationStatusTone =
                                  getQuotationStatusTone(cellData);
                                const normalizedBooleanFieldText =
                                  `${lowerFieldName} ${lowerDisplayName}`.replace(
                                    /[^a-z0-9]/g,
                                    "",
                                  );
                                const supplierEvaluationBooleanFieldTokens = [
                                  "customerrequiredsupplier",
                                  "solesource",
                                  "qualityappearsadequate",
                                  "deliveryavailability",
                                  "deliveryavailabilityandtermsareadequate",
                                  "referencesverified",
                                  "qualitymanagement",
                                  "qualitymanagementsystemcertification",
                                  "pricingvalue",
                                  "responsiveness",
                                  "resultsofevaluation",
                                  "resultsofevaluationofsamplepartsormaterials",
                                  "supplieriscapable",
                                  "supplieriscapableofmeetingregulatoryconcerns",
                                  "suppliersurveyresults",
                                  "resultsofonsite",
                                  "resultsofonsiteaudit",
                                ];
                                const isSupplierEvaluationBooleanField =
                                  supplierEvaluationBooleanFieldTokens.some(
                                    (token) =>
                                      normalizedBooleanFieldText.includes(
                                        token,
                                      ),
                                  );

                                const isBooleanField =
                                  !isDateLikeField &&
                                  !lowerFieldName.includes("_no") &&
                                  !lowerFieldName.endsWith("no") &&
                                  (heading.field_name
                                    .toLowerCase()
                                    .includes("revised") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("follow") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("quote") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("mr") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("mkt") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("prd") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("qa") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("pur") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("str") ||
                                    heading.field_name
                                      .toLowerCase()
                                      .includes("trg") ||
                                    isSupplierEvaluationBooleanField);

                                const normalizedBooleanValue = String(
                                  cellData ?? "",
                                )
                                  .trim()
                                  .toLowerCase();
                                const isCheckedBooleanValue = [
                                  "1",
                                  "true",
                                  "yes",
                                  "y",
                                ].includes(normalizedBooleanValue);

                                const isDeliveryDaysField =
                                  heading.field_name ===
                                  "day_difference_to_delivery";

                                const isFeasibilityField =
                                  heading.display_name
                                    ?.toLowerCase()
                                    .includes("feasibility") ||
                                  heading.field_name
                                    ?.toLowerCase()
                                    .includes("feasibility");
                                const feasibilityBackgroundColor =
                                  isFeasibilityField &&
                                  category !== "enquiry" &&
                                  cellData === "Not possible"
                                    ? "#fa8383"
                                    : undefined;

                                return (
                                  <td
                                    key={colIndex}
                                    className={`table-data${
                                      draggingColumnField === heading.field_name
                                        ? " analysis-column-dragging"
                                        : ""
                                    }`}
                                    data-field={heading.field_name}
                                    title={
                                      formattedCellData ||
                                      displayCellData ||
                                      (isRemarksField ? "" : "N/A")
                                    }
                                    style={{
                                      width: `${getColumnWidth(heading)}px`,
                                      minWidth: `${getColumnWidth(heading)}px`,
                                      maxWidth: `${getColumnWidth(heading)}px`,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      ...(colIndex === 0 &&
                                        rowBorderColor && {
                                          boxShadow: `inset 4px 0 0 ${rowBorderColor}`,
                                        }),
                                      ...(isStatusField &&
                                        !isQuotationStatusField && {
                                          color:
                                            cellData?.toLowerCase() ===
                                              "approved" ||
                                            cellData?.toLowerCase() ===
                                              "confirmed"
                                              ? "green"
                                              : cellData?.toLowerCase() ===
                                                  "rejected"
                                                ? "red"
                                                : cellData?.toLowerCase() ===
                                                    "hold"
                                                  ? "#f0ad4e"
                                                  : `var(--${
                                                      cellData
                                                        ?.toLowerCase()
                                                        ?.replace(
                                                          /\s+/g,
                                                          "_",
                                                        ) || "default"
                                                    })`,
                                        }),
                                    }}
                                  >
                                    {lowerFieldName.includes("revised") &&
                                    item.revision_no !== undefined &&
                                    item.revision_no !== null ? (
                                      <span
                                        className={`revision-chip${
                                          Number(item.is_latest ?? 1) !== 0
                                            ? " revision-chip--current"
                                            : ""
                                        }`}
                                        title={
                                          Number(item.is_latest ?? 1) !== 0
                                            ? "Latest revision"
                                            : "Superseded revision"
                                        }
                                      >
                                        R{Number(item.revision_no) || 0}
                                      </span>
                                    ) : isBooleanField ? (
                                      isCheckedBooleanValue ? (
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "100%",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: "18px",
                                              height: "18px",
                                              backgroundColor: "var(--primary)",
                                              border: "1px solid #ccc",
                                              borderRadius: "3px",
                                              position: "relative",
                                              cursor: "default",
                                            }}
                                          >
                                            <span
                                              style={{
                                                position: "absolute",
                                                left: "5px",
                                                top: "2px",
                                                width: "6px",
                                                height: "10px",
                                                border: "solid white",
                                                borderWidth: "0 2px 2px 0",
                                                transform: "rotate(45deg)",
                                              }}
                                            ></span>
                                          </div>
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "100%",
                                            fontSize: "16px",
                                          }}
                                        >
                                          -
                                        </div>
                                      )
                                    ) : isFeasibilityField && cellData ? (
                                      <span
                                        className={`status-pill ${
                                          String(cellData)
                                            .toLowerCase()
                                            .trim() === "possible"
                                            ? "status-pill--success"
                                            : String(cellData)
                                                  .toLowerCase()
                                                  .trim() === "not possible"
                                              ? "status-pill--danger"
                                              : "status-pill--neutral"
                                        }`}
                                      >
                                        {formattedCellData || cellData}
                                      </span>
                                    ) : isQuotationStatusField &&
                                      quotationStatusTone ? (
                                      <span
                                        className={`status-pill status-pill--quote-${quotationStatusTone}`}
                                      >
                                        {String(cellData)}
                                      </span>
                                    ) : isVoucherPaymentStatusField &&
                                      isEmptyListValue(cellData) ? (
                                      "Awaiting for Approval"
                                    ) : isStatusField &&
                                      !isEmptyListValue(cellData) ? (
                                      String(cellData).charAt(0).toUpperCase() +
                                      String(cellData).slice(1).toLowerCase()
                                    ) : formattedCellData ? (
                                      formattedCellData
                                    ) : isRemarksField ? (
                                      ""
                                    ) : (
                                      "N/A"
                                    )}
                                  </td>
                                );
                              })}
                              {trailingBodyHeadings.map((heading, colIndex) => {
                                const cellData = item[heading.field_name];
                                const formattedCellData = formatDate(cellData);
                                return (
                                  <td
                                    key={colIndex}
                                    data-field={heading.field_name}
                                    className={`table-data${
                                      draggingColumnField === heading.field_name
                                        ? " analysis-column-dragging"
                                        : ""
                                    }`}
                                    title={formattedCellData || "N/A"}
                                    style={{
                                      width: `${getColumnWidth(heading)}px`,
                                      minWidth: `${getColumnWidth(heading)}px`,
                                      maxWidth: `${getColumnWidth(heading)}px`,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {formattedCellData || "N/A"}
                                  </td>
                                );
                              })}
                              {Array.isArray(
                                tableData?.transactionSettings
                                  ?.dynamic_transaction,
                              ) &&
                                tableData.transactionSettings.dynamic_transaction.filter(
                                  (tran) => tran !== null && tran !== undefined,
                                ).length > 0 && (
                                  <td className="table-data">
                                    {tableData.transactionSettings.dynamic_transaction
                                      .filter(
                                        (tran) =>
                                          tran !== null && tran !== undefined,
                                      )
                                      .map((tran, index) => {
                                        const isSuperseded =
                                          item.is_latest !== undefined &&
                                          item.is_latest !== null &&
                                          Number(item.is_latest) === 0;
                                        const createdCodes = Array.isArray(
                                          item.dynamic_created_codes,
                                        )
                                          ? item.dynamic_created_codes.map(
                                              (code) =>
                                                String(code).toUpperCase(),
                                            )
                                          : [];
                                        const isAlreadyCreated =
                                          createdCodes.includes(
                                            String(
                                              tran?.tran_code || "",
                                            ).toUpperCase(),
                                          );
                                        return (
                                          <React.Fragment
                                            key={tran?.tran_code || index}
                                          >
                                            {isSuperseded ||
                                            isAlreadyCreated ? (
                                              <span
                                                className="create-transaction-link create-transaction-link--disabled"
                                                title={
                                                  isAlreadyCreated
                                                    ? `${tran?.tran_name} has already been created from this transaction`
                                                    : "Superseded revision — open the latest revision to create this"
                                                }
                                              >
                                                Create {tran?.tran_name}
                                              </span>
                                            ) : (
                                              <a
                                                href="#"
                                                className="create-transaction-link"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleCreateTransaction(
                                                    item,
                                                    tran?.tran_code,
                                                    tran?.tran_name,
                                                    tableData.transactionSettings,
                                                    transactionCode,
                                                  );
                                                }}
                                              >
                                                Create {tran?.tran_name}
                                              </a>
                                            )}
                                            {index <
                                              tableData.transactionSettings
                                                .dynamic_transaction.length -
                                                1 && <span>, </span>}
                                          </React.Fragment>
                                        );
                                      })}
                                  </td>
                                )}
                            </tr>
                            {isMasterProductsList &&
                              expandedProductRows[item.tran_id] && (
                                <tr className="product-bom-expanded-row">
                                  <td
                                    colSpan={
                                      (tableData?.headings?.filter(
                                        (heading) => !heading.is_internal,
                                      ).length || 0) + 1
                                    }
                                  >
                                    <div className="product-bom-container">
                                      {productBomCache[item.tran_id]
                                        ?.loading ? (
                                        <div className="product-bom-empty">
                                          Loading BOM…
                                        </div>
                                      ) : productBomCache[item.tran_id]
                                          ?.data ? (
                                        <>
                                          <div className="product-bom-header">
                                            <span className="product-bom-title">
                                              BILL OF MATERIALS
                                              {productBomCache[item.tran_id]
                                                .data.head_data?.tran_no
                                                ? ` \u2014 ${
                                                    productBomCache[
                                                      item.tran_id
                                                    ].data.head_data.tran_no
                                                  }`
                                                : ""}
                                            </span>
                                            {productBomCache[item.tran_id].data
                                              .head_data?.wo_tran_id && (
                                              <button
                                                type="button"
                                                className="product-bom-view-link"
                                                onClick={() =>
                                                  handleViewFullBom(
                                                    productBomCache[
                                                      item.tran_id
                                                    ].data.head_data,
                                                    item,
                                                  )
                                                }
                                              >
                                                View full BOM &rarr;
                                              </button>
                                            )}
                                          </div>
                                          <table className="product-bom-table">
                                            <thead>
                                              <tr>
                                                <th>SL</th>
                                                <th>MATERIAL / DESCRIPTION</th>
                                                <th>THICKNESS</th>
                                                <th>QTY</th>
                                                <th>BOM TYPE</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(
                                                productBomCache[item.tran_id]
                                                  .data.item_data || []
                                              ).map((material, mIndex) => (
                                                <tr key={mIndex}>
                                                  <td>
                                                    {material.serial_no ||
                                                      mIndex + 1}
                                                  </td>
                                                  <td>
                                                    {material.description ||
                                                      material.material ||
                                                      material.product_name ||
                                                      material.item_name ||
                                                      "N/A"}
                                                  </td>
                                                  <td>
                                                    {material.thickness ??
                                                      material.thick_mm ??
                                                      "\u2014"}
                                                  </td>
                                                  <td>
                                                    {material.total_quantity ??
                                                      material.quantity ??
                                                      material.qty ??
                                                      "\u2014"}
                                                  </td>
                                                  <td>
                                                    {material.bom_type ? (
                                                      <span className="product-bom-type-chip">
                                                        {material.bom_type}
                                                      </span>
                                                    ) : (
                                                      "\u2014"
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </>
                                      ) : (
                                        <div className="product-bom-empty">
                                          No BOM created yet
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            <tr className="table-border">
                              <td
                                colSpan={
                                  tableData?.headings?.length +
                                  (category === "enquiry" ? 1 : 0) +
                                  (isMasterProductsList ? 1 : 0)
                                }
                              ></td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            tableData?.headings?.length +
                            (category === "enquiry" ? 1 : 0) +
                            (isMasterProductsList ? 1 : 0)
                          }
                          style={{
                            textAlign: "center",
                            paddingTop: 70,
                            paddingBottom: 70,
                          }}
                        >
                          No Data Available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {supportsColumnSorting && (totalPages > 1 || totalCount > 0) && (
              <div className="list-pagination-bar">
                <span className="list-pagination-summary">
                  {paginationSummaryText}
                </span>
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={goToPage}
                  />
                )}
              </div>
            )}
          </div>
          {woPicker && (
            <div
              className="wo-picker-overlay"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget && !woPicker.creating) {
                  setWoPicker(null);
                }
              }}
            >
              <div className="wo-picker-modal" role="dialog" aria-modal="true">
                <div className="wo-picker-head">
                  <h3>Create Work Orders — Select Products</h3>
                  <button
                    className="wo-picker-close"
                    onClick={() => !woPicker.creating && setWoPicker(null)}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
                <p className="wo-picker-hint">
                  All products are pre-selected — untick any to exclude. One
                  Work Order is created per selected product.
                </p>
                <ul className="wo-picker-list">
                  {woPicker.items.map((item, index) => (
                    <li key={index}>
                      <label className="wo-picker-item">
                        <input
                          type="checkbox"
                          checked={woPicker.selected[index]}
                          disabled={woPicker.creating}
                          onChange={() =>
                            setWoPicker((prev) => ({
                              ...prev,
                              selected: prev.selected.map((sel, i) =>
                                i === index ? !sel : sel,
                              ),
                            }))
                          }
                        />
                        <span className="wo-picker-name">
                          {item.product_name ||
                            item.Product_Name ||
                            `Item ${index + 1}`}
                        </span>
                        <span className="wo-picker-qty">
                          {item.quantity || item.qty || "-"}{" "}
                          {item.uom || item.unit || ""}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                <div className="wo-picker-footer">
                  <button
                    className="wo-picker-cancel"
                    disabled={woPicker.creating}
                    onClick={() => setWoPicker(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="wo-picker-create"
                    disabled={
                      woPicker.creating ||
                      woPicker.selected.every((sel) => !sel)
                    }
                    onClick={handleBatchCreateWorkOrders}
                  >
                    {woPicker.creating
                      ? `Creating ${woPicker.progress} of ${woPicker.total}…`
                      : `Create Work Order (${
                          woPicker.selected.filter(Boolean).length
                        })`}
                  </button>
                </div>
              </div>
            </div>
          )}
          {category !== "drawing-status" && category !== "dr" && (
            <>
              <AddNewEntryModal
                isOpen={isProductModalOpen}
                onClose={handleCloseProductModal}
                onSubmit={handleProductSelect}
                transactionCode={transactionCode}
                heading="Please Select Category"
                placeholder="Category Name"
                searchPlaceholder="Search category name"
                options={products}
                onCompanySelect={
                  category === "purchase-enquiry" &&
                  tableData?.customerType === "vendor"
                    ? handleCompanySelect
                    : handleProductSelect
                }
                customerType={"product"}
                isPurchaseInquiryVendorFlow={
                  category === "purchase-enquiry" &&
                  tableData?.customerType === "vendor"
                }
              />

              <AddNewEntryModal
                isOpen={isSubcategoryModalOpen}
                onClose={handleCloseSubcategoryModal}
                onSubmit={() => {}}
                transactionCode={transactionCode}
                heading="Please Select Subcategory"
                placeholder="Subcategory"
                searchPlaceholder={`Search subcategory for ${selectedProductCategory}`}
                options={subcategories}
                onCompanySelect={handleSubcategorySelect}
                customerType={"subcategory"}
              />

              <AddNewEntryModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmitModal}
                transactionCode={transactionCode}
                heading={`Please Select ${
                  tableData?.customerType === "vendor" ? "Supplier" : "Customer"
                } Company`}
                placeholder="Company Name"
                searchPlaceholder="Search Company name"
                options={companies}
                onCompanySelect={handleCompanySelect}
                customerType={tableData?.customerType}
              />
            </>
          )}

          {isMasterDataModalOpen && (
            <Modal
              isOpen={isMasterDataModalOpen}
              onRequestClose={() => setIsMasterDataModalOpen(false)}
              className="transaction-other-detail-custom-modal"
              overlayClassName="transaction-other-detail-custom-overlay"
            >
              {transactionCode === "CM" ? (
                <CustomerMasterDataModal
                  transactionCode={transactionCode}
                  modeOfEdit="add"
                  onClose={() => setIsMasterDataModalOpen(false)}
                />
              ) : transactionCode === "VM" ? (
                <VendorMasterDataModal
                  transactionCode={transactionCode}
                  modeOfEdit="add"
                  onClose={() => setIsMasterDataModalOpen(false)}
                />
              ) : (
                <ProductMaster
                  transactionCode={transactionCode}
                  modeOfEdit="add"
                  onClose={() => setIsMasterDataModalOpen(false)}
                />
              )}
            </Modal>
          )}
        </div>
      )}
    </>
  );
};

export default TabContentDetail;
